import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { RegimeBadge } from '@/components/RegimeBadge';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Clock, CheckCircle, XCircle, AlertTriangle, RefreshCw, Edit3,
  User, Calendar, ChevronDown, ChevronUp, Save, Building2, Settings, Coffee, MapPin, History, Trash2, Plus, Loader, Map as MapIcon, ExternalLink
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/lib/AuthContext';
// 📏 [v13.63 Phase 5 — 2026-02-28] Bloc kilométrage admin par (employé, date)
import { usePermissions } from '@/lib/PermissionsContext';
import { KilometrageAdminBlock } from '@/components/KilometrageAdminBlock';
import { EmployeeAvatar } from '@/components/EmployeeAvatar';
import { BtSearchCombobox } from '@/components/BtSearchCombobox';
import { formatLocalTime, formatLocalDate } from '@/utils/formatLocalTime'; // 🆕 [P1 ext]
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { ValidationPunchMapDialog } from '@/components/validation/ValidationPunchMapDialog';
import { estProprietaire, aLeRole } from '@/lib/roles'; // 👥 E3b — rôles multiples
import { formatItemHoraire } from '@/lib/formatItemHoraire'; // 🆕 [v13.60.2 — 2026-08-12] Libellé cohérent items cédulés

const API_URL = (typeof window !== 'undefined' ? window.__API_URL__ : process.env.REACT_APP_BACKEND_URL) || '';

/**
 * Formate un datetime ISO en "YYYY-MM-DDThh:mm" pour <input type="datetime-local">
 */
const toDatetimeLocal = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    const pad = n => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  } catch { return ''; }
};

/**
 * Calcule les heures nettes à partir des temps IN/OUT et de la pause
 */
const calcHeures = (punchIn, punchOut, heuresPause) => {
  try {
    const t1 = new Date(punchIn);
    const t2 = new Date(punchOut);
    const h = (t2 - t1) / 3600000 - parseFloat(heuresPause || 0);
    return Math.max(0, Math.round(h * 4) / 4); // arrondi au quart d'heure
  } catch { return 0; }
};

// ─── Ligne de punch éditable ─────────────────────────────────────────────────
function DeductionLunchBanner({ punch, onUpdate }) {
  const [approving, setApproving] = useState(false);
  const mins = punch.deduction_proposee_minutes;
  const h = Math.floor(mins / 60);
  const m = Math.round(mins % 60);
  const distKm = punch.deduction_proposee_distance_km;

  const handleApprouver = async () => {
    setApproving(true);
    try {
      const res = await axios.post(`${API_URL}/api/punch/${punch.id}/approuver-deduction`);
      const data = res.data;
      if (data.status === 'success') {
        toast.success(`Déduction approuvée: −${h > 0 ? `${h}h` : ''}${m}min`);
        onUpdate();
      } else {
        toast.error(data.detail || 'Erreur');
      }
    } catch { toast.error('Erreur réseau'); }
    finally { setApproving(false); }
  };

  return (
    <div className="border-t border-amber-300 bg-amber-50 px-4 py-2 flex items-center gap-3 flex-wrap" data-testid={`deduction-lunch-${punch.id}`}>
      <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0" />
      <p className="text-xs text-amber-800 flex-1">
        <span className="font-semibold">Dépunch hors chantier détecté</span>
        {distKm && <span className="ml-1">— à {distKm.toFixed(1)} km du chantier.</span>}
        <span className="ml-1">Déduction proposée: <strong>−{h > 0 ? `${h}h ` : ''}{m}min</strong> (trajet aller à 50 km/h)</span>
      </p>
      <div className="flex gap-2">
        <Button size="sm" className="h-7 text-xs bg-amber-600 hover:bg-amber-700" onClick={handleApprouver} disabled={approving} data-testid={`btn-approuver-deduction-${punch.id}`}>
          {approving ? 'Approbation...' : 'Approuver'}
        </Button>
        <Button size="sm" variant="outline" className="h-7 text-xs" onClick={async () => {
          await axios.put(`${API_URL}/api/punch/${punch.id}/adjust`, { deduction_proposee_minutes: 0 });
          onUpdate();
        }}>
          Ignorer
        </Button>
      </div>
    </div>
  );
}

// ─── Haversine rapide (distance en mètres entre 2 coords GPS) ───────────────
function haversineMeters(lat1, lon1, lat2, lon2) {
  if (lat1 == null || lon1 == null || lat2 == null || lon2 == null) return null;
  const R = 6371000;
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

const formatDistance = (m) => {
  if (m == null || isNaN(m)) return '—';
  return m >= 1000 ? `${(m / 1000).toFixed(2)} km` : `${Math.round(m)} m`;
};

// ─── Panneau "Hors zone" — déduction + avertissement (Phase 2) ──────────────
function HorsZoneDecisionPanel({ punch, bureauCoords, onUpdate }) {
  const initialMinutes =
    typeof punch.deduction_minutes_approuvees === 'number'
      ? punch.deduction_minutes_approuvees
      : (punch.deduction_proposee_minutes || 0);
  const [editMinutes, setEditMinutes] = useState(initialMinutes);
  const [busy, setBusy] = useState(null); // 'approve_ded' | 'refuse_ded' | 'approve_warn' | 'give_chance'

  const pw = punch.pending_warning || null;
  const distanceChantier =
    punch.deduction_proposee_distance_km != null
      ? punch.deduction_proposee_distance_km * 1000
      : (pw?.distance_metres ?? punch.geo_check?.distance_metres ?? null);

  // Distance au bureau (Haversine sur coord de punch-out)
  const distanceBureau = (() => {
    if (!bureauCoords) return null;
    const lat = punch.punch_out_latitude ?? punch.punch_in_latitude;
    const lng = punch.punch_out_longitude ?? punch.punch_in_longitude;
    return haversineMeters(lat, lng, bureauCoords.latitude, bureauCoords.longitude);
  })();

  const strikes = punch.strikes_hors_zone_count || 0;
  const deductionDecidee = punch.deduction_approuvee != null;
  const warningDecide = pw && pw.statut && pw.statut !== 'en_attente';

  const callEndpoint = async (action, body = null) => {
    setBusy(action);
    try {
      const url = `${API_URL}/api/punch/${punch.id}/${action}`;
      const res = body ? await axios.post(url, body) : await axios.post(url);
      return res.data;
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || 'Erreur';
      toast.error(`Erreur: ${detail}`);
      return null;
    } finally {
      setBusy(null);
    }
  };

  const handleApproveDeduction = async () => {
    if (editMinutes < 0) { toast.error('Minutes invalides'); return; }
    const r = await callEndpoint('approve_deduction', { minutes: Number(editMinutes) });
    if (r?.status === 'success') {
      toast.success(`Déduction approuvée : −${Number(editMinutes).toFixed(1)} min`);
      onUpdate && onUpdate();
    }
  };
  const handleRefuseDeduction = async () => {
    const r = await callEndpoint('refuse_deduction');
    if (r?.status === 'success') {
      toast.info('Déduction refusée');
      onUpdate && onUpdate();
    }
  };
  const handleApproveWarning = async () => {
    const r = await callEndpoint('approve_warning');
    if (r?.status === 'success') {
      const smsMsg = r.sms_status === 'preview_simulated'
        ? ' · SMS simulé (PREVIEW_MODE)'
        : r.sms_status === 'sent' ? ' · SMS envoyé' : '';
      toast.success(`Avertissement créé${smsMsg}`);
      onUpdate && onUpdate();
    }
  };
  const handleGiveChance = async () => {
    const r = await callEndpoint('give_chance');
    if (r?.status === 'success') {
      if (r.action === 'auto_strike_triggered') {
        toast.warning(`3 indulgences atteintes → avertissement automatique créé (compteur reset)`);
      } else {
        toast.info(`Indulgence accordée (strikes: ${r.strikes}/3)`);
      }
      onUpdate && onUpdate();
    }
  };

  return (
    <div
      className="border-t border-red-300 bg-red-50 px-4 py-3 space-y-3"
      data-testid={`hors-zone-panel-${punch.id}`}
    >
      {/* Header */}
      <div className="flex items-start gap-2">
        <MapPin className="w-4 h-4 shrink-0 mt-0.5 text-red-600" />
        <div className="flex-1">
          <p className="text-xs font-semibold text-red-800">
            🚨 Punch géolocalisé hors zone
          </p>
          <div className="text-xs text-red-700 mt-1 flex flex-wrap gap-x-4 gap-y-1">
            <span>📍 Chantier : <strong>{formatDistance(distanceChantier)}</strong></span>
            {distanceBureau != null && (
              <span>🏢 Bureau : <strong>{formatDistance(distanceBureau)}</strong></span>
            )}
          </div>
        </div>
      </div>

      {/* Section Déduction */}
      <div className="bg-white rounded-md border border-red-200 p-3">
        <p className="text-xs font-semibold text-slate-700 mb-2">⏱️ Déduction de temps</p>
        {deductionDecidee ? (
          <p className="text-xs text-slate-500 italic">
            {punch.deduction_approuvee
              ? `✅ Déduction approuvée (−${punch.deduction_minutes_approuvees ?? '—'} min)`
              : '❌ Déduction refusée'}
          </p>
        ) : (
          <div className="flex flex-wrap items-center gap-2">
            <Label className="text-xs text-slate-600">Minutes à déduire :</Label>
            <Input
              type="number"
              min={0}
              step="0.5"
              value={editMinutes}
              onChange={(e) => setEditMinutes(e.target.value)}
              className="w-24 h-8 text-xs"
              data-testid={`input-deduction-${punch.id}`}
            />
            <Button
              size="sm"
              className="h-8 bg-emerald-600 hover:bg-emerald-700 text-white"
              onClick={handleApproveDeduction}
              disabled={!!busy}
              data-testid={`btn-approve-ded-${punch.id}`}
            >
              {busy === 'approve_ded' ? <Loader className="w-3 h-3 animate-spin" /> : '✅'} Approuver
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="h-8 border-slate-300 text-slate-700"
              onClick={handleRefuseDeduction}
              disabled={!!busy}
              data-testid={`btn-refuse-ded-${punch.id}`}
            >
              ❌ Refuser
            </Button>
            {punch.deduction_proposee_minutes > 0 && (
              <span className="text-[11px] text-slate-400">
                (suggéré : {punch.deduction_proposee_minutes} min)
              </span>
            )}
          </div>
        )}
      </div>

      {/* Section Avertissement */}
      {pw && (
        <div className="bg-white rounded-md border border-red-200 p-3">
          <p className="text-xs font-semibold text-slate-700 mb-1">⚠️ Avertissement</p>
          <p className="text-[11px] text-slate-500 mb-2">
            {pw.description || `Punch hors zone à ${formatDistance(distanceChantier)}`}
          </p>
          <p className="text-[11px] text-slate-500 mb-2">
            Indulgences accordées à cet employé : <strong>{strikes}/3</strong>
            {strikes >= 2 && (
              <span className="ml-2 text-amber-700 font-semibold">
                ⚠️ Prochaine indulgence → avertissement automatique
              </span>
            )}
          </p>
          {warningDecide ? (
            <p className="text-xs text-slate-500 italic">
              {pw.statut === 'approuve' && '✅ Avertissement approuvé'}
              {pw.statut === 'indulge' && '💚 Chance accordée'}
              {pw.statut === 'auto_strike' && '⚠️ Avertissement auto (3 strikes)'}
            </p>
          ) : (
            <div className="flex flex-wrap gap-2">
              <Button
                size="sm"
                className="h-8 bg-red-600 hover:bg-red-700 text-white"
                onClick={handleApproveWarning}
                disabled={!!busy}
                data-testid={`btn-approve-warn-${punch.id}`}
              >
                {busy === 'approve_warn' ? <Loader className="w-3 h-3 animate-spin" /> : '⚠️'} Approuver l'avertissement
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="h-8 border-emerald-400 text-emerald-700 hover:bg-emerald-50"
                onClick={handleGiveChance}
                disabled={!!busy}
                data-testid={`btn-give-chance-${punch.id}`}
              >
                {busy === 'give_chance' ? <Loader className="w-3 h-3 animate-spin" /> : '💚'} Donner une chance
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// 🧱 Refactor BT Phase 4 — 4.4 (B-2) : libellés FR-CA des raisons `hors_assignation_detail.raison`
export const LIBELLES_HORS_ASSIGNATION = {
  non_rattache: "l'employé n'était pas assigné à ce bon de travail",
  exclu_explicitement: "l'employé avait été retiré de ce bon de travail",
  hors_dates_assignees: "l'employé n'était pas prévu ce jour-là",
  hors_dates_chantier: 'le bon de travail ne prévoyait pas de chantier ce jour-là',
  chef_principal_hors_jour: "un autre chef était prévu ce jour-là",
  chef_principal_hors_dates_assignees: "le chef n'était pas prévu ce jour-là",
  termine: 'le bon de travail était terminé',
  bt_termine: 'le bon de travail était terminé',
  bt_annule: 'le bon de travail était annulé',
};


function PunchRow({ punch, onValidate, onRefuse, onUpdate, adminLocked, isOwner, lavageJourInfo, bureauCoords, isSelected, onToggleSelect }) {  const [editing, setEditing] = useState(false);
  const [punchIn, setPunchIn] = useState(toDatetimeLocal(punch.punch_in_time));
  const [punchOut, setPunchOut] = useState(toDatetimeLocal(punch.punch_out_time));
  // 🔁 RÉGRESSION B3 — Default = pause RÉELLE saisie au punch (ou 0 si rien),
  // PAS un fallback de 0.5h qui forcerait une pause auto pour les admins.
  const [heuresPause, setHeuresPause] = useState(punch.heures_pause ?? 0);
  // 🆕 [Phase v13.52 — 2026-07-20] Temps de déplacement (minutes int).
  // Convention Q2 : un champ par journée stocké sur le premier punch.
  // Convention Q3 : attaché au BT courant. Q1 : édition Owner/CP/adjoint uniquement.
  const [tempsDeplacement, setTempsDeplacement] = useState(
    Number.isFinite(Number(punch.temps_deplacement_minutes)) ? Number(punch.temps_deplacement_minutes) : 0
  );
  const [notes, setNotes] = useState(punch.notes_validation || '');
  const [saving, setSaving] = useState(false);
  const [showGps, setShowGps] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [confirmValidate, setConfirmValidate] = useState(false);
  const [deleting, setDeleting] = useState(false);
  // 🗺️ [Phase v13.41 — 2026-07-14] Dialog carte punch + client (2 épingles)
  const [mapDialog, setMapDialog] = useState({ open: false, type: 'in' });

  // ✅ FIX 2026-05-06 (Phase 4) — Persistance "j'ai revu ce punch GPS"
  // localStorage par punch.id : permet de basculer orange→rouge/gris après clic admin.
  const [gpsReviewed, setGpsReviewed] = useState(() => {
    try { return localStorage.getItem(`gps-reviewed-${punch.id}`) === '1'; }
    catch { return false; }
  });
  const [recheckingGeo, setRecheckingGeo] = useState(false);
  const handleRecheckGeo = async () => {
    setRecheckingGeo(true);
    try {
      const res = await axios.post(`${API_URL}/api/punch/${punch.id}/recheck-geo`);
      const data = res.data;
      if (data?.status === 'success') {
        const horsZone = data.geo_check?.hors_zone;
        if (horsZone === false) toast.success('✅ GPS vérifié : dans la zone');
        else if (horsZone === true) toast.warning(`🚨 Hors zone — ${Math.round(data.geo_check.distance_metres)} m`);
        else toast.info(`Vérification toujours impossible : ${data.geo_check?.reason || 'inconnu'}`);
        onUpdate && onUpdate();
      } else {
        toast.error(data?.detail || 'Erreur lors de la re-vérification');
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur réseau');
    } finally {
      setRecheckingGeo(false);
    }
  };

  // ✅ Association à un Bon de Travail depuis ValidationHeures
  // 🆕 2026-05-08 — Délégué au composant unifié BtSearchCombobox.
  const [btId, setBtId] = useState(punch.bon_travail_id || '');
  // 🆕 2026-05-14 — Permettre aussi de rattacher le punch à un Service Call
  //   (cas typique : SC créé après coup, on retombe sur des heures admin déjà punchées).
  const [scId, setScId] = useState(punch.service_call_id || '');
  const [scList, setScList] = useState([]);
  const [scLoading, setScLoading] = useState(false);
  useEffect(() => {
    let cancelled = false;
    setScLoading(true);
    axios.get(`${API_URL}/api/crm/service-calls`)
      .then(r => {
        if (cancelled) return;
        const all = r.data?.service_calls || [];
        // Pertinence : SCs récents (90j) OU déjà liés à cet employé
        const cutoff = new Date();
        cutoff.setDate(cutoff.getDate() - 90);
        const filtered = all
          .filter(sc => {
            const refDate = sc.date_prevue || sc.date_service || sc.created_at || '';
            if (!refDate) return true;
            return new Date(refDate) >= cutoff;
          })
          .sort((a, b) => (b.date_prevue || b.created_at || '').localeCompare(a.date_prevue || a.created_at || ''));
        setScList(filtered);
      })
      .catch(() => setScList([]))
      .finally(() => { if (!cancelled) setScLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const hasGps = !!(punch.punch_in_latitude || punch.punch_out_latitude);

  const heuresCalc = punchOut ? calcHeures(
    new Date(punchIn).toISOString(),
    new Date(punchOut).toISOString(),
    heuresPause
  ) : null;

  /** Validation/Refus rapide (sans ajustement de temps) */
  const handleQuickValidation = async (valide) => {
    setSaving(true);
    try {
      const token = localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token');
      if (!token) { toast.error('Session expirée — reconnectez-vous'); setSaving(false); return; }
      const res = await axios.put(`${API_URL}/api/punch/${punch.id}/adjust`, 
        { valide },
        { headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` } }
      );
      if (res.status !== 200) {
        const msg = res.data?.detail || `Erreur serveur (${res.status})`;
        toast.error(msg);
        return;
      }
      toast.success(valide ? 'Pointage validé' : 'Pointage refusé');
      onUpdate();
    } catch (e) {
      toast.error('Erreur réseau — vérifiez votre connexion');
    } finally { setSaving(false); }
  };

  /** Validation/Refus AVEC ajustements de temps (depuis le panneau d'édition) */
  const handleAdjustAndValidate = async (valide) => {
    setSaving(true);
    try {
      const token = localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token');
      if (!token) { toast.error('Session expirée — reconnectez-vous'); setSaving(false); return; }
      const body = { valide, notes_validation: notes, heures_pause: parseFloat(heuresPause) };
      if (punchIn) body.punch_in_time = new Date(punchIn).toISOString();
      if (punchOut) body.punch_out_time = new Date(punchOut).toISOString();
      // ✅ Toujours transmettre le bon_travail_id choisi (ou null pour dissocier)
      body.bon_travail_id = btId || null;
      // 🆕 2026-05-14 — Transmettre aussi le service_call_id si l'admin choisit
      //   de rattacher le punch à un SC (au lieu d'un BT).
      body.service_call_id = scId || null;
      // 🆕 [Phase v13.52 — 2026-07-20] Temps de déplacement (Q1 — Owner/CP/adjoint).
      body.temps_deplacement_minutes = Math.max(0, parseInt(tempsDeplacement, 10) || 0);
      const res = await axios.put(`${API_URL}/api/punch/${punch.id}/adjust`, 
        body,
        { headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` } }
      );
      if (res.status !== 200) {
        const msg = res.data?.detail || `Erreur serveur (${res.status})`;
        toast.error(msg);
        return;
      }
      toast.success(valide ? 'Pointage validé' : 'Pointage refusé');
      setEditing(false);
      onUpdate();
    } catch (e) {
      toast.error('Erreur réseau — vérifiez votre connexion');
    } finally { setSaving(false); }
  };

  const handleSaveOnly = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token');
      const body = { notes_validation: notes, heures_pause: parseFloat(heuresPause) };
      if (punchIn) body.punch_in_time = new Date(punchIn).toISOString();
      if (punchOut) body.punch_out_time = new Date(punchOut).toISOString();
      body.bon_travail_id = btId || null;
      body.service_call_id = scId || null;
      // 🆕 [Phase v13.52 — 2026-07-20] Temps de déplacement sauvegardable sans validation.
      body.temps_deplacement_minutes = Math.max(0, parseInt(tempsDeplacement, 10) || 0);
      await axios.put(`${API_URL}/api/punch/${punch.id}/adjust`, 
        body,
        { headers: { 'Content-Type': 'application/json', ...(token && { Authorization: `Bearer ${token}` }) } }
      );
      toast.success('Ajustements sauvegardés');
      setEditing(false);
      onUpdate();
    } catch { toast.error('Erreur réseau'); }
    finally { setSaving(false); }
  };

  const handleDeletePunch = async () => {
    setDeleting(true);
    try {
      const token = localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token');
      const res = await axios.delete(`${API_URL}/api/punch/${punch.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.status === 200) {
        toast.success('Pointage supprimé');
        setConfirmDelete(false);
        onUpdate();
      } else {
        toast.error(res.data?.detail || 'Erreur lors de la suppression');
      }
    } catch (e) { 
      toast.error(e.response?.data?.detail || 'Erreur réseau'); 
    }
    finally { setDeleting(false); }
  };

  const isOubliDepunch = !punch.punch_out_time;

  return (
    <div className={`border rounded-lg overflow-hidden ${isOubliDepunch ? 'border-orange-300' : 'border-gray-200'}`}>
      {/* Row principale — 🐛 [Phase Hotfix Bug B — 2026-07-09] Fix layout : nom client toujours visible.
          Avant : `flex-wrap` + `truncate` sur span contexte coupait le nom quand la ligne
          était saturée de badges. Après : span contexte a `min-w-[220px]` + `break-words` → nom
          client jamais tronqué (peut wrapper sur 2 lignes mais reste 100 % lisible). */}
      <div className={`flex flex-wrap items-center gap-3 px-3 py-2.5 ${isOubliDepunch ? 'bg-orange-50' : 'bg-white'}`}>
        {/* 🆕 [Phase v13.60.17 — 2026-08-18] Checkbox de sélection batch.
            Désactivée si punch incomplet (pas de punch_out_time) ou adminLocked (peintre → admin locked).
            Cohérent avec handleValidateAll qui exclut aussi les punches incomplets. */}
        {onToggleSelect && !adminLocked && (
          <Checkbox
            checked={!!isSelected}
            disabled={!punch.punch_out_time}
            onCheckedChange={() => onToggleSelect(punch.id)}
            aria-label={`Sélectionner le punch de ${punch.employee_name}`}
            data-testid={`checkbox-select-punch-${punch.id}`}
            className="shrink-0"
          />
        )}
        {/* Employé */}
        <div className="flex items-center gap-2 min-w-[140px]">
          <EmployeeAvatar photoUrl={punch.employee_photo_url} prenom={punch.employee_name?.split(' ')[0] || ''} nom={punch.employee_name?.split(' ').slice(1).join(' ') || ''} size="xs" />
          <div className="flex flex-col leading-tight">
            <span className="font-medium text-sm">{punch.employee_name}</span>
            {punch.employee_role && (
              <span className="text-[10px] text-slate-500">{punch.employee_role}</span>
            )}
          </div>
        </div>

        {/* Contexte — 🐛 [Phase Hotfix Bug B — 2026-07-09] Retrait de `truncate` + min-w garanti.
            Avant : `flex-1 truncate` coupait le nom client avec `...` dès que la ligne saturait.
            Après : `flex-1 min-w-[220px] break-words` → nom client TOUJOURS lisible en entier
            (peut wrapper sur 2 lignes si nécessaire). */}
        <span className="text-xs text-gray-500 flex-1 min-w-[220px] break-words leading-snug">
          {/* 🔄 Phase A2.3 — Si type_punch=service_call, afficher contexte SC */}
          {punch.type_punch === 'service_call' ? (
            <>
              <Badge
                variant="outline"
                className="mr-2 text-orange-700 border-orange-400 bg-orange-50 text-xs font-semibold"
                title={punch.service_call_description_full || 'Service Call'}
                data-testid={`badge-sc-${punch.id}`}
              >
                🔧 SC
              </Badge>
              <span title={punch.service_call_description_full}>
                {/* 🆕 [v13.60.2] Libellé cohérent via helper (fallback safe, jamais d'ID technique) */}
                {formatItemHoraire(punch)}
                {punch.bon_travail_nom && (
                  <span className="text-orange-700 font-medium"> · BT « {punch.bon_travail_nom} »</span>
                )}
                {punch.service_call_description && (
                  <span className="text-slate-400 italic"> — {punch.service_call_description}</span>
                )}
              </span>
              {punch.service_call_dans_3_mois === false && (
                <Badge
                  variant="outline"
                  className="ml-2 text-slate-600 border-slate-300 bg-slate-50 text-[10px]"
                  title="SC créé > 90 jours après fermeture du BT — exclu du critère performance"
                >
                  hors 3 mois
                </Badge>
              )}
            </>
          ) : (
            <>
              {/* 🆕 [v13.60.2] Libellé cohérent — remplace la chaîne de fallback qui pouvait afficher
                  "Route lavage 2026-08-12" (contrat_nom brut) au lieu du nom client. */}
              {formatItemHoraire(punch)}
              {punch.type_punch === 'admin' && (
                <Badge variant="outline" className="ml-2 text-purple-700 border-purple-300 text-xs">admin</Badge>
              )}
              {/* 🆕 2026-05-06 Phase 6 Module CCQ — badge régime */}
              {punch.bt_regime_ccq && (
                <RegimeBadge
                  regime={punch.bt_regime_ccq}
                  requiresReview={punch.ccq_snapshot?.requires_ccq_grade_review === true}
                  className="ml-2"
                />
              )}
              {punch.type_punch !== 'admin' && !punch.bon_travail_id && !punch.client_nom && (
                <Badge variant="outline" className="ml-2 text-amber-700 border-amber-400 bg-amber-50 text-xs font-semibold" title="Aucun bon de travail associé — cliquez sur le crayon pour associer">
                  BT non associé
                </Badge>
              )}
              {/* 🧱 Refactor BT Phase 4 — 4.4 (B-2) : punch sur un BT où l'employé n'était pas assigné ce jour-là */}
              {punch.hors_assignation === true && (
                <Badge
                  variant="outline"
                  data-testid="badge-punch-hors-assignation"
                  className="ml-2 text-orange-800 border-orange-400 bg-orange-50 text-xs font-semibold"
                  title={`Hors assignation — ${LIBELLES_HORS_ASSIGNATION[punch.hors_assignation_detail?.raison] || punch.hors_assignation_detail?.raison || 'non assigné ce jour-là'} (BT ${punch.hors_assignation_detail?.bt_id || '?'}, ${punch.hors_assignation_detail?.date || '?'})`}
                >
                  Hors assignation
                </Badge>
              )}
            </>
          )}
          {punch.auto_modifie && (
            <Badge variant="outline" className="ml-2 text-orange-700 border-orange-400 bg-orange-50 text-xs font-semibold" data-testid={`badge-auto-modifie-${punch.id}`}>
              Modifié par {punch.auto_modifie_par || 'employé'}
            </Badge>
          )}
        </span>

        {/* Heures display */}
        <div className="flex items-center gap-2 text-xs">
          <span className="text-gray-500">
            {formatLocalTime(punch.punch_in_time, { timeOnly: true })}
            {' → '}
            {punch.punch_out_time
              ? formatLocalTime(punch.punch_out_time, { timeOnly: true })
              : <span className="text-orange-600 font-semibold">Pas de punch OUT</span>
            }
          </span>
          {punch.punch_out_time && punch.heures_pause > 0 && (() => {
            const pauseTotalMin = Math.round((punch.heures_pause || 0) * 60);
            const pausePayeeMin = Math.round((punch.pause_payee || 0) * 60);
            const pauseDeductionMin = Math.round((punch.pause_deduction || (punch.heures_pause - (punch.pause_payee || 0))) * 60);
            return (
              <div className="flex items-center gap-1" data-testid={`pause-summary-${punch.id}`}>
                {/* 🆕 [Phase 44 BIS #1] Affichage cohérent : on montre TOUJOURS un badge
                    de statut pour clarifier ce qui se passe avec la pause (déduit / payé /
                    non décomptée). Avant : si pause_payee=0 ET pause_deduction=0, l'utilisatrice
                    voyait juste un badge ambré « P:Xm » sans contexte clair. */}
                <span
                  className="text-amber-700 bg-amber-50 border border-amber-200 px-1.5 rounded text-[10px] font-semibold"
                  title={`Pause totale prise: ${pauseTotalMin} min`}
                  data-testid={`badge-pause-prise-${punch.id}`}
                >
                  {pauseTotalMin}m prise
                </span>
                {pauseDeductionMin > 0 ? (
                  <span
                    className="text-red-700 bg-red-50 border border-red-200 px-1.5 rounded text-[10px] font-semibold"
                    title={`${pauseDeductionMin} min déduites du salaire (pause non payée)`}
                    data-testid={`badge-pause-deduction-${punch.id}`}
                  >
                    −{pauseDeductionMin}m déduit
                  </span>
                ) : pausePayeeMin > 0 ? (
                  <span
                    className="text-emerald-700 bg-emerald-50 border border-emerald-200 px-1.5 rounded text-[10px] font-semibold"
                    title={`${pausePayeeMin} min de pause payées (selon le rôle)`}
                    data-testid={`badge-pause-payee-${punch.id}`}
                  >
                    +{pausePayeeMin}m payé
                  </span>
                ) : (
                  <span
                    className="text-slate-600 bg-slate-50 border border-slate-200 px-1.5 rounded text-[10px]"
                    title="Pause prise mais ni déduite ni payée explicitement"
                    data-testid={`badge-pause-non-decomptee-${punch.id}`}
                  >
                    0m déduite (non décomptée)
                  </span>
                )}
                {/* Cas mixte : déduit ET payé (rare) → afficher le 3e badge */}
                {pauseDeductionMin > 0 && pausePayeeMin > 0 && (
                  <span
                    className="text-emerald-700 bg-emerald-50 border border-emerald-200 px-1.5 rounded text-[10px] font-semibold"
                    title={`${pausePayeeMin} min de pause payées en plus`}
                  >
                    +{pausePayeeMin}m payé
                  </span>
                )}
              </div>
            );
          })()}
          {/* 🆕 Badge "Total pause journée" pour les punchs de route de lavage */}
          {punch.type_punch === 'lavage' && lavageJourInfo && lavageJourInfo.totalPauseMin > 0 && (
            <span
              className="text-cyan-700 bg-cyan-50 border border-cyan-200 px-1.5 rounded text-[10px] font-medium"
              title={`Route de lavage — ${lavageJourInfo.clientsCount} client(s) ce jour. La pause obligatoire de 30 min a été répartie également sur chaque client de la route.`}
              data-testid={`badge-pause-journee-lavage-${punch.id}`}
            >
              🧽 Total pause jour : {lavageJourInfo.totalPauseMin} min ({lavageJourInfo.clientsCount} clients)
            </span>
          )}
          {/* 🆕 [Phase 80.1+80.2 — 2026-06-22] Badge "🧪 TEST" pour les punchs créés
              via le bouton de test Owner-only. Phase 80.2 : punch persiste pour
              audit Rapport d'heures (suppression manuelle Owner). */}
          {punch.is_test_punch && (
            <span
              className="text-orange-700 bg-orange-100 border border-orange-300 px-1.5 rounded text-[10px] font-bold animate-pulse"
              title="Punch de test (Owner-only) — visible aussi dans Rapport d'heures avec metadata d'audit"
              data-testid={`badge-punch-test-${punch.id}`}
            >
              🧪 TEST
            </span>
          )}
          {/* 🆕 [Phase 80 — 2026-06-22] Badges Pause PM — 6 statuts (au lieu de 2).
              Source unique : `punch.pause_pm_evaluation` (sous-objet rempli au dépunch
              OU via migration Phase 80 sur l'historique). Fallback legacy si absent. */}
          {(() => {
            // Helper format humain : 7.84h → "7h50"
            const fmtHHMM = (h) => {
              if (h == null || isNaN(h)) return '—';
              const totalMin = Math.round(h * 60);
              const hh = Math.floor(totalMin / 60);
              const mm = totalMin % 60;
              return `${hh}h${String(mm).padStart(2, '0')}`;
            };
            const ev = punch.pause_pm_evaluation || null;
            // ─── 1. Si l'évaluation existe → décide via les champs structurés
            if (ev) {
              // 1.A Réponse "oui" → pause prise
              if (ev.reponse_employe === 'oui') {
                return (
                  <span
                    className="text-emerald-700 bg-emerald-50 border border-emerald-200 px-1.5 rounded text-[10px] font-medium"
                    title={`Pause PM prise — déclarée par l'employé au dépunch. Total à la montre : ${fmtHHMM(ev.total_brut_jour_h_au_depunch)}.`}
                    data-testid={`badge-pause-pm-oui-${punch.id}`}
                  >
                    ☕ Pause PM : Question posée — Oui ({fmtHHMM(ev.total_brut_jour_h_au_depunch)})
                  </span>
                );
              }
              // 1.B Réponse "non" → pause non prise + 15 min payées
              if (ev.reponse_employe === 'non') {
                return (
                  <span
                    className="text-amber-700 bg-amber-50 border border-amber-200 px-1.5 rounded text-[10px] font-medium"
                    title={`Pause PM non prise — déclarée par l'employé au dépunch. ${ev.minutes_ajoutees || 15} min payées ajoutées au total. Total à la montre : ${fmtHHMM(ev.total_brut_jour_h_au_depunch)}.`}
                    data-testid={`badge-pause-pm-non-${punch.id}`}
                  >
                    ☕ Pause PM : Question posée — Non · +{ev.minutes_ajoutees || 15} min payées
                  </span>
                );
              }
              // 1.C Raison documentée mais pas de réponse
              const raison = ev.raison_non_admissible;
              if (raison === 'moins_de_7h35') {
                return (
                  <span
                    className="text-slate-600 bg-slate-100 border border-slate-200 px-1.5 rounded text-[10px] font-medium"
                    title={`Total à la montre : ${fmtHHMM(ev.total_brut_jour_h_au_depunch)}. Seuil pause PM : 7h35.`}
                    data-testid={`badge-pause-pm-sous-seuil-${punch.id}`}
                  >
                    ☕ Pause PM : Non admissible — {fmtHHMM(ev.total_brut_jour_h_au_depunch)} ({'<'} 7h35)
                  </span>
                );
              }
              if (raison === 'type_emploi' || raison === 'punch_admin' || raison === 'route_lavage_du_jour') {
                const label = raison === 'route_lavage_du_jour' ? 'route de lavage du jour' : (raison === 'punch_admin' ? 'punch admin' : "type d'emploi");
                return (
                  <span
                    className="text-slate-500 bg-slate-50 border border-slate-200 px-1.5 rounded text-[10px] font-medium"
                    title={`Pause PM non applicable — ${label}.`}
                    data-testid={`badge-pause-pm-non-eligible-${punch.id}`}
                  >
                    ☕ Pause PM : Non admissible — {label}
                  </span>
                );
              }
              if (raison === 'reponse_manquante') {
                return (
                  <span
                    className="text-red-700 bg-red-50 border border-red-200 px-1.5 rounded text-[10px] font-semibold"
                    title={`La modale s'est affichée mais l'employé n'a pas répondu (cas zombie). Vérification recommandée.`}
                    data-testid={`badge-pause-pm-zombie-${punch.id}`}
                  >
                    ⚠️ Pause PM : Réponse manquante
                  </span>
                );
              }
              if (raison === 'punch_legacy' || raison === 'avant_date_demarrage') {
                return (
                  <span
                    className="text-slate-400 bg-slate-50 border border-slate-100 px-1.5 rounded text-[10px] font-medium"
                    title={`Punch antérieur à l'introduction de la pause PM ou information de traçabilité manquante.`}
                    data-testid={`badge-pause-pm-legacy-${punch.id}`}
                  >
                    ☕ Pause PM : Non évaluée (legacy)
                  </span>
                );
              }
              // 🐛 [Phase Hotfix Bug A — 2026-07-09] Badge dédié import manuel
              if (raison === 'import_manuel') {
                return (
                  <span
                    className="text-blue-700 bg-blue-50 border border-blue-200 px-1.5 rounded text-[10px] font-medium"
                    title={`Punch créé via import manuel (correction admin). La question Pause PM n'est pas posée dans ce cas.`}
                    data-testid={`badge-pause-pm-import-manuel-${punch.id}`}
                  >
                    📥 Pause PM : Import manuel (correction)
                  </span>
                );
              }
              // Cas par défaut : autres raisons (déjà_declaree, pas_dernier_punch, erreur_calcul, auto_depunch_pas_de_modale)
              return (
                <span
                  className="text-slate-500 bg-slate-50 border border-slate-200 px-1.5 rounded text-[10px] font-medium"
                  title={`Raison : ${raison || 'non documentée'}.`}
                  data-testid={`badge-pause-pm-autre-${punch.id}`}
                >
                  ☕ Pause PM : {raison || 'Non évaluée'}
                </span>
              );
            }
            // ─── 2. Fallback legacy : `pause_pm_evaluation` ABSENT → utilise les anciens champs
            if (punch.pause_pm_applicable && punch.pause_pm_prise === true) {
              return (
                <span className="text-emerald-700 bg-emerald-50 border border-emerald-200 px-1.5 rounded text-[10px] font-medium"
                  title="L'employé a déclaré avoir pris sa pause de 15 min en après-midi au moment du dépunch."
                  data-testid={`badge-pause-pm-prise-${punch.id}`}>
                  ☕ Pause PM : prise (legacy)
                </span>
              );
            }
            if (punch.pause_pm_applicable && punch.pause_pm_prise === false) {
              return (
                <span className="text-amber-700 bg-amber-50 border border-amber-200 px-1.5 rounded text-[10px] font-medium"
                  title="L'employé a déclaré ne pas avoir pris sa pause de 15 min. +15 min ajoutées."
                  data-testid={`badge-pause-pm-non-prise-${punch.id}`}>
                  ☕ Pause PM : non prise — +15 min (legacy)
                </span>
              );
            }
            // 3. Aucune donnée du tout (vieux punch sans pause_pm_*) → afficher legacy non évaluée
            return (
              <span className="text-slate-400 bg-slate-50 border border-slate-100 px-1.5 rounded text-[10px] font-medium"
                title="Aucune donnée pause PM disponible (punch pré-Phase 80)."
                data-testid={`badge-pause-pm-data-absente-${punch.id}`}>
                ☕ Pause PM : Non évaluée
              </span>
            );
          })()}
          {/* 🆕 [9.1] Alerte coords identiques in/out — cache GPS suspect */}
          {punch.geo_alerte === 'coords_identiques_suspect' && (
            <span
              className="text-red-700 bg-red-50 border border-red-300 px-1.5 rounded text-[10px] font-semibold"
              title="Les coordonnées du punch IN et du punch OUT sont strictement identiques (5 décimales) malgré un écart de temps important. Cause probable : cache GPS du navigateur. Vérification recommandée."
              data-testid={`badge-geo-coords-identiques-${punch.id}`}
            >
              ⚠️ Coords identiques in/out — vérification recommandée
            </span>
          )}
          {punch.punch_out_time && (
            <span className="font-semibold text-blue-700">
              {(punch.heures_nettes ?? 0).toFixed(2).replace('.', ',')}h
            </span>
          )}
        </div>

        {/* Date */}
        <span className="text-xs text-gray-400">
          {formatLocalTime(punch.punch_in_time, { dateOnly: true, format: 'short', withoutYear: true })}
        </span>

        {/* Actions */}
        <div className="flex items-center gap-1 ml-auto flex-shrink-0">
          {hasGps && (() => {
            // ✅ FIX 2026-05-14 — Logique élargie à 4 états :
            // - 🟢 Vert   : en zone (vérifié OK)
            // - 🟢 Vert   : "Présumé OK" → chantier non géocodable MAIS GPS du
            //   téléphone reçu (la vérif auto est impossible côté serveur, mais
            //   la position du téléphone est crédible — l'utilisateur n'a rien
            //   à valider manuellement par défaut)
            // - 🔴 Rouge  : faute réelle (hors zone confirmé OU GPS du téléphone
            //   manquant)
            // - 🟠 Orange : vraie ambigüité (geo_check absent / autre raison) —
            //   demande validation manuelle
            // 🆕 2026-05-15 — Tenir compte de geo_check_in (vérification du
            // punch IN, désormais effectuée en temps réel). On combine les 2 :
            //   • Si l'un des 2 est hors_zone → rouge
            //   • Si les 2 sont OK (ou présumés OK) → vert
            //   • Si aucun n'a été vérifié → orange
            const gOut = punch.geo_check || {};
            // 🆕 2026-05-15 v2 — Fallback symétrique pour le IN (cf. modal détaillé)
            let gIn = punch.geo_check_in || {};
            if (!punch.geo_check_in && gOut.reason === 'no_site_coords') {
              gIn = { reason: 'no_site_coords', hors_zone: null };
            }
            const horsZoneOut = gOut.hors_zone === true;
            const horsZoneIn = gIn.hors_zone === true;
            const dansZoneOut = gOut.hors_zone === false;
            const dansZoneIn = gIn.hors_zone === false;
            const reasonOut = gOut.reason;
            const reasonIn = gIn.reason;
            const noPunchGps = reasonOut === 'no_punch_gps' || reasonIn === 'no_punch_gps';
            const fauteReelle = horsZoneOut || horsZoneIn || noPunchGps;
            const presumeOk =
              (reasonOut === 'no_site_coords' && !!punch.punch_out_latitude) ||
              (reasonIn === 'no_site_coords' && !!punch.punch_in_latitude);
            const aucuneVerif = !punch.geo_check && !punch.geo_check_in;

            let colorClass, titre;
            if (fauteReelle) {
              colorClass = 'text-red-600 bg-red-100 hover:bg-red-200 ring-2 ring-red-400';
              if (horsZoneOut || horsZoneIn) {
                const dist = horsZoneOut ? gOut.distance_metres : gIn.distance_metres;
                const distLabel = dist >= 1000 ? `${(dist / 1000).toFixed(1)} km` : `${Math.round(dist)} m`;
                const which = horsZoneIn && !horsZoneOut ? 'au punch IN' : (horsZoneOut && !horsZoneIn ? 'au punch OUT' : 'aux deux punches');
                titre = `🚨 Hors zone ${which} — ${distLabel} du chantier`;
              } else {
                titre = "🚨 GPS du téléphone manquant — l'employé n'a pas partagé sa position";
              }
            } else if (dansZoneOut || dansZoneIn) {
              colorClass = 'text-emerald-600 bg-emerald-50 hover:bg-emerald-100 ring-1 ring-emerald-300';
              titre = '✓ Dans la zone du chantier';
            } else if (presumeOk) {
              // Chantier non géocodable + GPS téléphone reçu → présumé OK
              colorClass = 'text-emerald-600 bg-emerald-50 hover:bg-emerald-100 ring-1 ring-emerald-300';
              titre = "✓ Présumé OK — chantier non géocodable mais GPS du téléphone reçu (aucune validation manuelle requise)";
            } else if (reasonOut === 'office_tolerance' || reasonIn === 'office_tolerance') {
              colorClass = 'text-emerald-600 bg-emerald-50 hover:bg-emerald-100 ring-1 ring-emerald-300';
              titre = "✓ Tolérance bureau (chef d'équipe)";
            } else if (aucuneVerif) {
              // 🟠 Vraie ambigüité (aucune vérification stockée)
              colorClass = 'text-orange-600 bg-orange-100 hover:bg-orange-200 ring-2 ring-orange-400 animate-pulse';
              titre = "⚠️ Vérification GPS non effectuée — cliquer pour valider manuellement";
            } else {
              // 🟢 Tout est OK par défaut (verifié mais pas dans la zone explicitement, ex: presume_ok au IN seulement)
              colorClass = 'text-emerald-600 bg-emerald-50 hover:bg-emerald-100 ring-1 ring-emerald-300';
              titre = '✓ Position GPS vérifiée';
            }
            return (
              <Button
                size="sm"
                variant="ghost"
                className={`h-7 w-7 p-0 ${colorClass}`}
                title={titre}
                onClick={() => setShowGps(true)}
                data-testid={`btn-voir-gps-${punch.id}`}
              >
                <MapPin className="w-3.5 h-3.5" />
              </Button>
            );
          })()}
          <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-blue-500"
            onClick={() => setEditing(!editing)}>
            <Edit3 className="w-3.5 h-3.5" />
          </Button>
          {adminLocked ? (
            <span className="text-xs text-gray-400 italic px-2">Propriétaire seulement</span>
          ) : (
            <>
              <Button size="sm" variant="ghost" className="h-7 px-1.5 sm:px-2 text-green-600 hover:bg-green-50"
                onClick={() => setConfirmValidate(true)} disabled={saving || !punch.punch_out_time}
                data-testid={`btn-valider-${punch.id}`}>
                <CheckCircle className="w-3.5 h-3.5 sm:mr-1" /> <span className="hidden sm:inline">Valider</span>
              </Button>
              <Button size="sm" variant="ghost" className="h-7 px-1.5 sm:px-2 text-red-500 hover:bg-red-50"
                onClick={() => handleAdjustAndValidate(false)} disabled={saving}
                data-testid={`btn-refuser-${punch.id}`}
                title="Refuse le punch en appliquant les ajustements de pause/temps">
                <XCircle className="w-3.5 h-3.5 sm:mr-1" /> <span className="hidden sm:inline">Refuser</span>
              </Button>
            </>
          )}
          {isOwner && (
            <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-gray-400 hover:text-red-600 hover:bg-red-50"
              onClick={() => setConfirmDelete(true)} title="Supprimer ce pointage"
              data-testid={`btn-supprimer-${punch.id}`}>
              <Trash2 className="w-3.5 h-3.5" />
            </Button>
          )}
        </div>
      </div>

      {/* Confirmation de suppression */}
      {confirmDelete && (
        <div className="border-t bg-red-50 px-4 py-3 flex items-center justify-between">
          <span className="text-sm text-red-800 font-medium">
            Supprimer ce pointage de {punch.employee_nom || punch.employee_name} ?
          </span>
          <div className="flex gap-2">
            <Button size="sm" variant="destructive" onClick={handleDeletePunch} disabled={deleting}
              data-testid={`btn-confirmer-suppression-${punch.id}`}>
              {deleting ? 'Suppression...' : 'Confirmer la suppression'}
            </Button>
            <Button size="sm" variant="outline" onClick={() => setConfirmDelete(false)}
              data-testid={`btn-annuler-suppression-${punch.id}`}>
              Annuler
            </Button>
          </div>
        </div>
      )}

      {/* Bannière déduction lunch hors zone */}
      {/* Ancienne bannière déduction lunch (masquée si punch hors zone — géré par HorsZoneDecisionPanel) */}
      {punch.deduction_proposee_minutes > 0 && !punch.deduction_approuvee && !punch.geo_check?.hors_zone && !punch.pending_warning && (
        <DeductionLunchBanner punch={punch} onUpdate={onUpdate} />
      )}

      {/* Dialog confirmation validation */}
      {confirmValidate && (
        <div className="border-t border-green-200 bg-green-50 px-4 py-3 flex items-center justify-between gap-3" data-testid={`confirm-validate-${punch.id}`}>
          <div>
            <p className="text-sm font-semibold text-green-800">Confirmer la validation</p>
            <p className="text-xs text-green-600 mt-0.5">
              Valider le pointage de <strong>{punch.employee_nom}</strong> — {punch.heures_nettes?.toFixed(2)}h nettes ?
            </p>
          </div>
          <div className="flex gap-2 shrink-0">
            <Button size="sm" className="bg-green-600 hover:bg-green-700 h-8"
              onClick={() => { setConfirmValidate(false); handleAdjustAndValidate(true); }}
              disabled={saving}
              data-testid={`btn-confirmer-validation-${punch.id}`}>
              <CheckCircle className="w-3.5 h-3.5 mr-1" /> Oui, valider
            </Button>
            <Button size="sm" variant="outline" className="h-8"
              onClick={() => setConfirmValidate(false)}>
              Non
            </Button>
          </div>
        </div>
      )}

      {/* Note de l'employé / Modification soumise */}
      {(punch.note_employe || punch.modification_soumise || punch.statut === 'modification_soumise') && (
        <div className={`border-t px-4 py-2 flex items-start gap-2 ${punch.statut === 'modification_soumise' ? 'border-orange-300 bg-orange-50' : 'border-blue-300 bg-blue-50'}`} data-testid={`note-employe-${punch.id}`}>
          <AlertTriangle className={`w-4 h-4 shrink-0 mt-0.5 ${punch.statut === 'modification_soumise' ? 'text-orange-600' : 'text-blue-600'}`} />
          <div>
            <p className={`text-xs font-semibold ${punch.statut === 'modification_soumise' ? 'text-orange-800' : 'text-blue-800'}`}>
              {punch.statut === 'modification_soumise' ? 'Modification soumise par l\'employé :' : 'Note de l\'employé :'}
            </p>
            <p className={`text-xs ${punch.statut === 'modification_soumise' ? 'text-orange-700' : 'text-blue-700'}`}>{punch.note_employe}</p>
            {punch.note_employe_date && (
              <p className="text-[10px] text-slate-400 mt-0.5">
                Soumis le {formatLocalDate(punch.note_employe_date)} à {formatLocalTime(punch.note_employe_date, { timeOnly: true })}
              </p>
            )}
          </div>
        </div>
      )}

      {/* Panneau Hors Zone — Phase 2 : déduction + avertissement avec décisions */}
      {(punch.geo_check?.hors_zone || punch.deduction_proposee_minutes > 0 || punch.pending_warning) && (
        <HorsZoneDecisionPanel punch={punch} bureauCoords={bureauCoords} onUpdate={onUpdate} />
      )}

      {/* 🆕 Alerte Multi-BT suspect : l'employé avait ≥2 BT prévus ce jour mais un seul punch */}
      {punch.multi_bt_suspect && punch.multi_bt_suspect.nb_bt_prevus >= 2 && (
        <div
          className="border-t border-amber-300 bg-amber-50 px-4 py-2 flex items-start gap-2"
          data-testid={`alerte-multi-bt-${punch.id}`}
        >
          <span className="text-base shrink-0" aria-hidden>✂️</span>
          <div className="flex-1">
            <p className="text-xs font-semibold text-amber-800">
              ⚠️ Multi-BT suspect : {punch.multi_bt_suspect.nb_bt_prevus} bons de travail prévus ce jour pour cet employé
            </p>
            <p className="text-xs text-amber-700 mt-1">
              L'employé a peut-être oublié de dé-puncher en changeant de chantier. Pensez à <b>segmenter</b> ce punch après validation depuis « Rapport d'heures ».
            </p>
            <ul className="text-[11px] text-amber-900 mt-1.5 space-y-0.5">
              {punch.multi_bt_suspect.bts_prevus.map((bt) => {
                const estActuel = bt.bt_id === punch.multi_bt_suspect.bt_actuel_id;
                return (
                  <li key={bt.bt_id} className="flex items-center gap-1.5">
                    <span className={estActuel ? 'text-green-700 font-semibold' : 'text-amber-900'}>
                      {estActuel ? '✓' : '•'} {bt.client_nom}
                    </span>
                    {estActuel && <span className="text-[10px] text-green-600">(punch actuel)</span>}
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      )}

      {/* 🗺️ [v13.63 Phase 3 — 2026-02-28] Sélection manuelle par l'employé
          L'employé s'est auto-assigné via « Rechercher mon chantier » — laisse
          une trace visible pour le validateur. */}
      {punch.assignation_manuelle_employe === true && (
        <div
          className="border-t border-violet-300 bg-violet-50 px-4 py-2 flex items-start gap-2"
          data-testid={`alerte-selection-manuelle-${punch.id}`}
        >
          <span className="text-base shrink-0" aria-hidden>🔎</span>
          <div className="flex-1">
            <p className="text-xs font-semibold text-violet-800">
              Sélection manuelle par l'employé
              {punch.assignation_manuelle_at && (
                <> — <span className="font-normal">{formatLocalDate(punch.assignation_manuelle_at)} à {formatLocalTime(punch.assignation_manuelle_at, { timeOnly: true })}</span></>
              )}
            </p>
            <p className="text-xs text-violet-700 mt-0.5">
              L'employé n'était pas cédulé à ce chantier — il s'est ajouté lui-même via « Rechercher mon chantier ». Vérifiez la cohérence avec la cédule officielle.
            </p>
          </div>
        </div>
      )}

      {/* Panneau d'édition */}
      {editing && (
        <div className="border-t bg-gray-50 px-4 py-3 space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
            <div>
              <Label className="text-xs">Punch IN <span className="text-red-500">*</span></Label>
              <input
                type="datetime-local"
                value={punchIn}
                onChange={e => setPunchIn(e.target.value)}
                className="w-full border rounded-md px-2 py-1 text-sm"
                step="60"
              />
            </div>
            <div>
              <Label className="text-xs">Punch OUT</Label>
              <input
                type="datetime-local"
                value={punchOut}
                onChange={e => setPunchOut(e.target.value)}
                className="w-full border rounded-md px-2 py-1 text-sm"
                step="60"
              />
            </div>
            <div>
              <Label className="text-xs">Pause (minutes)</Label>
              <input
                type="number"
                value={Math.round((parseFloat(heuresPause) || 0) * 60)}
                onChange={e => {
                  const minutes = parseFloat(e.target.value) || 0;
                  setHeuresPause((minutes / 60).toFixed(4));
                }}
                min="0" step="5"
                className="w-full border rounded-md px-2 py-1 text-sm"
                data-testid={`input-pause-min-${punch.id}`}
                placeholder="Ex: 27"
              />
              <p className="text-[10px] text-slate-400 mt-0.5">
                = {(parseFloat(heuresPause) || 0).toFixed(2)}h
              </p>
            </div>
            <div>
              <Label className="text-xs">Heures nettes calculées</Label>
              <div className={`border rounded-md px-2 py-1 text-sm font-bold h-9 flex items-center ${heuresCalc !== null && heuresCalc < 0 ? 'text-red-600' : 'text-blue-700'}`}>
                {heuresCalc !== null ? `${heuresCalc.toFixed(2).replace('.', ',')}h` : '—'}
              </div>
            </div>
          </div>
          {/* 🚚 [Phase v13.52 — 2026-07-20] Temps de déplacement (minutes int).
              Compte comme heures régulières dans TOUTES les agrégations (paie,
              matrice BT, bonus, seuil 40h supp, seuil pause PM 7h35).
              Édition réservée Owner/CP/adjoint (Q1) — guard backend au PUT. */}
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label className="text-xs flex items-center gap-1">
                <span title="Temps rémunéré pour le trajet vers le chantier (compte comme heures régulières)">
                  Temps déplacement (min)
                </span>
              </Label>
              <input
                type="number"
                value={tempsDeplacement}
                onChange={e => {
                  const raw = parseInt(e.target.value, 10);
                  setTempsDeplacement(Number.isFinite(raw) && raw >= 0 ? raw : 0);
                }}
                min="0" step="5"
                className="w-full border rounded-md px-2 py-1 text-sm"
                data-testid={`input-temps-deplacement-${punch.id}`}
                placeholder="Ex: 45"
              />
              <p className="text-[10px] text-slate-400 mt-0.5">
                = {((parseInt(tempsDeplacement, 10) || 0) / 60).toFixed(2)}h régulières
              </p>
            </div>
            <div className="col-span-2">
              <Label className="text-xs">Total heures rémunérées</Label>
              <div className="border rounded-md px-2 py-1 text-sm font-bold h-9 flex items-center text-green-700">
                {heuresCalc !== null
                  ? `${(heuresCalc + (parseInt(tempsDeplacement, 10) || 0) / 60).toFixed(2).replace('.', ',')}h`
                  : '—'}
                <span className="text-[10px] text-slate-400 font-normal ml-2">
                  = heures nettes + déplacement
                </span>
              </div>
            </div>
          </div>
          <div>
            <Label className="text-xs">Notes (optionnel)</Label>
            <Input value={notes} onChange={e => setNotes(e.target.value)} placeholder="Note de validation..." className="text-sm" />
          </div>

          {/* ✅ Suggestion de déduction (distance-based) directement dans le crayon */}
          {punch.deduction_proposee_minutes > 0 && !punch.deduction_approuvee && (
            <div className="border border-amber-300 bg-amber-50 rounded-md px-3 py-2 flex items-start gap-2" data-testid={`suggestion-deduction-crayon-${punch.id}`}>
              <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
              <div className="flex-1 text-xs text-amber-900">
                <p className="font-semibold">💡 Déduction suggérée : −{punch.deduction_proposee_minutes} min</p>
                <p className="text-amber-700 mt-0.5">
                  Distance dépunch ↔ chantier : {punch.deduction_proposee_distance_km?.toFixed(1)} km
                  (trajet aller simple à 50 km/h)
                </p>
              </div>
              <Button
                size="sm"
                type="button"
                className="h-7 text-xs bg-amber-600 hover:bg-amber-700"
                data-testid={`btn-appliquer-deduction-crayon-${punch.id}`}
                onClick={() => {
                  // Ajoute les minutes proposées à la pause actuelle (déduction = plus de pause)
                  const currentMins = Math.round((parseFloat(heuresPause) || 0) * 60);
                  const newMins = currentMins + punch.deduction_proposee_minutes;
                  setHeuresPause((newMins / 60).toFixed(4));
                  toast.info(`+${punch.deduction_proposee_minutes} min ajoutés à la pause. Cliquez sur Enregistrer.`);
                }}
              >
                Appliquer
              </Button>
            </div>
          )}

          {/* ✅ Associer le punch à un Bon de Travail (composant unifié) */}
          <div>
            <Label className="text-xs flex items-center gap-1 mb-1">
              Bon de travail {!btId && !scId && <span className="text-amber-600 text-[10px]">(Non associé)</span>}
              {scId && <span className="text-yellow-700 text-[10px]">(remplacé par un Service Call)</span>}
            </Label>
            <BtSearchCombobox
              value={btId}
              onChange={(id) => { setBtId(id || ''); if (id) setScId(''); }}
              referenceDate={(punch.punch_in_time || '').slice(0, 10)}
              testIdPrefix={`bt-validation-${punch.id}`}
            />
          </div>

          {/* 🆕 2026-05-14 — Rattacher à un Service Call (alternatif au BT).
              Idéal pour les heures admin/chantier déjà punchées avant la création du SC. */}
          <div>
            <Label className="text-xs flex items-center gap-1 mb-1">
              <span className="inline-block">🔧 Service Call (alternatif)</span>
              {btId && <span className="text-blue-600 text-[10px]">(retire le BT si choisi)</span>}
            </Label>
            <Select
              value={scId || '__none__'}
              onValueChange={(v) => {
                const newSc = v === '__none__' ? '' : v;
                setScId(newSc);
                if (newSc) setBtId('');
              }}
            >
              <SelectTrigger className="h-9 text-xs" data-testid={`sc-validation-${punch.id}`}>
                <SelectValue placeholder={scLoading ? 'Chargement des SCs…' : 'Aucun SC associé'} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">— Aucun SC —</SelectItem>
                {scList.map(sc => (
                  <SelectItem key={sc.id} value={sc.id}>
                    {(sc.date_prevue || sc.date_service || '').slice(0, 10)} · {sc.client_nom || sc.nom_dossier || 'Client inconnu'} — {(sc.description || '').slice(0, 50)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {scList.length === 0 && !scLoading && (
              <p className="text-[10px] text-slate-400 mt-1">Aucun Service Call récent (90 derniers jours).</p>
            )}
          </div>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={handleSaveOnly} disabled={saving}>
              <Save className="w-3 h-3 mr-1" /> Sauvegarder ajustements
            </Button>
            <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={() => handleAdjustAndValidate(true)} disabled={saving || !punchOut}>
              <CheckCircle className="w-3 h-3 mr-1" /> Valider avec ces heures
            </Button>
            <Button size="sm" variant="ghost" className="text-red-500" onClick={() => handleAdjustAndValidate(false)} disabled={saving}>
              <XCircle className="w-3 h-3 mr-1" /> Refuser
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setEditing(false)} className="ml-auto">Annuler</Button>
          </div>
        </div>
      )}

      {/* Dialog GPS */}
      {showGps && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setShowGps(false)}>
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full mx-4 p-6" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold flex items-center gap-2">
                <MapPin className="w-4 h-4 text-emerald-600" />
                Localisation — {punch.employee_name}
              </h3>
              <button onClick={() => setShowGps(false)} className="text-gray-400 hover:text-gray-600 text-xl leading-none">×</button>
            </div>
            <div className="space-y-3 text-sm">
              {/* Légende de statut GPS */}
              <div className="flex items-center gap-3 text-[11px] text-slate-500 border-b pb-2">
                <span className="flex items-center gap-1">
                  <span className="w-2.5 h-2.5 rounded-full bg-emerald-500" /> Dans la zone
                </span>
                <span className="flex items-center gap-1">
                  <span className="w-2.5 h-2.5 rounded-full bg-red-500" /> Hors zone
                </span>
                <span className="flex items-center gap-1">
                  <span className="w-2.5 h-2.5 rounded-full bg-amber-500" /> Non vérifié
                </span>
                {punch.geo_check?.distance_metres !== undefined && punch.geo_check?.distance_metres !== null && (
                  <span className="ml-auto font-semibold text-slate-700">
                    Distance: {punch.geo_check.distance_metres >= 1000
                      ? `${(punch.geo_check.distance_metres / 1000).toFixed(2)} km`
                      : `${Math.round(punch.geo_check.distance_metres)} m`}
                  </span>
                )}
              </div>

              {/* Bannière "Vérification impossible" — Phase 3 (2026-05-06)
                  ✅ FIX 2026-05-07 : style informatif (bleu) quand c'est juste l'adresse
                  qui n'est pas géocodée (le punch est OK). Reste ambre seulement si
                  le GPS du téléphone est manquant (vraie alerte). */}
              {punch.geo_check?.hors_zone === null && punch.geo_check?.reason && punch.geo_check.reason !== 'office_tolerance' && (() => {
                const isAlerteReelle = punch.geo_check.reason === 'no_punch_gps';
                const containerCls = isAlerteReelle
                  ? 'rounded-lg p-3 bg-amber-50 border-2 border-amber-300'
                  : 'rounded-lg p-3 bg-blue-50 border border-blue-200';
                const titleCls = isAlerteReelle
                  ? 'font-semibold text-amber-800 text-xs flex items-center gap-1.5'
                  : 'font-medium text-blue-800 text-xs flex items-center gap-1.5';
                const textCls = isAlerteReelle ? 'text-amber-700 text-xs mt-1' : 'text-blue-700 text-xs mt-1';
                const titleText = isAlerteReelle
                  ? '⚠️ GPS du dépunch manquant — réviser manuellement'
                  : 'ℹ️ Vérification GPS automatique impossible';
                return (
                  <div className={containerCls} data-testid={`banner-geo-non-verifie-${punch.id}`}>
                    <p className={titleCls}>{titleText}</p>
                    <p className={textCls}>
                      {punch.geo_check.reason === 'no_punch_gps' && "Le téléphone n'a pas transmis de coordonnées GPS au dépunch (permission refusée, GPS désactivé, ou bug app)."}
                      {punch.geo_check.reason === 'no_site_coords' && "L'adresse du chantier sur ce BT n'a pas pu être géocodée — le punch reste présumé OK puisque le GPS du téléphone a été reçu."}
                    </p>
                    {punch.punch_out_latitude && punch.bon_travail_id && (
                      <Button
                        size="sm"
                        variant="outline"
                        className={`mt-2 h-7 text-xs ${isAlerteReelle ? 'border-amber-400 text-amber-800 hover:bg-amber-100' : 'border-blue-400 text-blue-700 hover:bg-blue-100'}`}
                        onClick={handleRecheckGeo}
                        disabled={recheckingGeo}
                        data-testid={`btn-recheck-geo-${punch.id}`}
                      >
                        {recheckingGeo
                          ? <><Loader className="w-3 h-3 mr-1 animate-spin" /> Vérification…</>
                          : <><RefreshCw className="w-3 h-3 mr-1" /> Réessayer la vérification GPS</>
                        }
                      </Button>
                    )}
                  </div>
                );
              })()}

              {punch.punch_in_latitude && (() => {
                // 🆕 2026-05-15 — Le punch IN a maintenant son propre geo_check_in
                // (vérifié au moment du punch IN, pas seulement à la sortie).
                // États possibles : OK / Hors zone / Non vérifié.
                // 🆕 2026-05-15 v2 — Fallback symétrique : si le punch_out a
                // déjà un geo_check avec reason='no_site_coords', on infère le
                // même statut pour le IN (même BT, donc même résolution
                // d'adresse). Évite l'incohérence "IN orange / OUT vert" qui
                // frustre l'utilisateur sur les anciens punches.
                let ginInfo = punch.geo_check_in || {};
                const gOutInfo = punch.geo_check || {};
                if (
                  !punch.geo_check_in &&
                  gOutInfo.reason === 'no_site_coords'
                ) {
                  // Hériter du OUT : chantier non géocodable → présumé OK pour IN aussi
                  ginInfo = { reason: 'no_site_coords', hors_zone: null };
                }
                const isHorsZone = ginInfo.hors_zone === true;
                const isDansZone = ginInfo.hors_zone === false;
                const reasonIn = ginInfo.reason;
                const presumeOkIn = reasonIn === 'no_site_coords' && !!punch.punch_in_latitude;
                const okFinal = isDansZone || presumeOkIn || reasonIn === 'office_tolerance';
                const cls = isHorsZone
                  ? 'bg-red-100 border-2 border-red-500'
                  : okFinal
                    ? 'bg-emerald-50 border border-emerald-200'
                    : 'bg-amber-50 border border-amber-300';
                const txtCls = isHorsZone ? 'text-red-700' : okFinal ? 'text-emerald-700' : 'text-amber-700';
                const badge = isHorsZone
                  ? `Hors zone · ${(ginInfo.distance_metres >= 1000 ? `${(ginInfo.distance_metres / 1000).toFixed(1)} km` : `${Math.round(ginInfo.distance_metres || 0)} m`)}`
                  : okFinal ? 'OK' : 'Non vérifié';
                const badgeCls = isHorsZone
                  ? 'bg-red-600 text-white'
                  : okFinal
                    ? 'bg-emerald-600 text-white'
                    : 'bg-amber-500 text-white';
                return (
                  <div className={`rounded-lg p-3 ${cls}`} data-testid={`gps-card-in-${punch.id}`}>
                    <div className="flex items-center justify-between mb-1">
                      <p className={`font-semibold ${txtCls}`}>
                        {isHorsZone ? '🚨' : okFinal ? '✓' : '⚠️'} Punch IN — {formatLocalTime(punch.punch_in_time, { timeOnly: true })}
                      </p>
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${badgeCls}`}>
                        {badge}
                      </span>
                    </div>
                    {punch.punch_in_adresse && <p className="text-gray-600 text-xs mb-2">{punch.punch_in_adresse}</p>}
                    {presumeOkIn && (
                      <p className="text-blue-700 text-[11px] mb-1.5 italic">ℹ️ Chantier non géocodable — présumé OK (GPS téléphone valide reçu)</p>
                    )}
                    <div className="flex flex-wrap items-center gap-2">
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        className="h-7 gap-1 text-xs border-red-300 text-red-700 hover:bg-red-50"
                        onClick={() => setMapDialog({ open: true, type: 'in' })}
                        data-testid={`btn-voir-carte-in-${punch.id}`}
                      >
                        <MapIcon className="w-3.5 h-3.5" />
                        Voir sur la carte
                      </Button>
                      <a
                        href={`https://www.google.com/maps?q=${punch.punch_in_latitude},${punch.punch_in_longitude}`}
                        target="_blank" rel="noreferrer"
                        className="inline-flex items-center gap-1 text-[11px] text-gray-500 hover:text-blue-700 hover:underline"
                        data-testid={`gps-link-in-${punch.id}`}
                      >
                        <ExternalLink className="w-3 h-3" />
                        Google Maps ({punch.punch_in_latitude?.toFixed(5)}, {punch.punch_in_longitude?.toFixed(5)})
                      </a>
                    </div>
                  </div>
                );
              })()}
              {punch.punch_out_latitude && (() => {
                // ✅ FIX 2026-05-14 — Logique élargie à 4 états :
                //   - isHorsZone (hors_zone===true) → rouge "Hors zone"
                //   - isDansZone (hors_zone===false) → vert "OK"
                //   - reason='no_site_coords' avec GPS du téléphone reçu →
                //     VERT "Présumé OK" (l'adresse chantier n'est pas géocodable,
                //     on ne peut PAS vérifier automatiquement, mais le GPS du
                //     téléphone est valide → on présume OK)
                //   - reason='no_punch_gps' → rouge "GPS manquant"
                //   - sinon (geo_check absent, vraie ambigüité) → ambre "À valider"
                const isHorsZone = punch.geo_check?.hors_zone === true;
                const isDansZone = punch.geo_check?.hors_zone === false;
                const reason = punch.geo_check?.reason;
                const noPunchGps = reason === 'no_punch_gps';
                const presumeOk = reason === 'no_site_coords' && !!punch.punch_out_latitude;
                const fauteReelle = isHorsZone || noPunchGps;
                const okFinal = isDansZone || presumeOk;
                const cls = fauteReelle
                  ? 'bg-red-100 border-2 border-red-500'
                  : okFinal
                  ? 'bg-emerald-50 border border-emerald-200'
                  : 'bg-amber-50 border-2 border-amber-300';
                const textCls = fauteReelle ? 'text-red-700' : okFinal ? 'text-emerald-700' : 'text-amber-700';
                const badgeCls = fauteReelle ? 'bg-red-600 text-white' : okFinal ? 'bg-emerald-600 text-white' : 'bg-amber-500 text-white';
                const badgeLabel = isHorsZone
                  ? 'Hors zone'
                  : noPunchGps
                  ? 'GPS manquant'
                  : presumeOk
                  ? 'Présumé OK'
                  : isDansZone
                  ? 'OK'
                  : 'À valider';
                const icon = fauteReelle ? '🚨' : okFinal ? '✓' : '⚠️';
                return (
                  <div className={`rounded-lg p-3 ${cls}`} data-testid={`gps-card-out-${punch.id}`}>
                    <div className="flex items-center justify-between mb-1">
                      <p className={`font-semibold ${textCls}`}>
                        {icon} Punch OUT — {punch.punch_out_time ? formatLocalTime(punch.punch_out_time, { timeOnly: true }) : '—'}
                      </p>
                      {isHorsZone ? (
                        <span
                          className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-red-600 text-white uppercase tracking-wide animate-pulse"
                          data-testid={`badge-hors-zone-${punch.id}`}
                        >
                          🚨 Hors zone · {punch.geo_check.distance_metres >= 1000
                            ? `${(punch.geo_check.distance_metres / 1000).toFixed(1)} km`
                            : `${Math.round(punch.geo_check.distance_metres)} m`}
                        </span>
                      ) : (
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${badgeCls}`}>
                          {badgeLabel}
                        </span>
                      )}
                    </div>
                    {punch.punch_out_adresse && <p className="text-gray-600 text-xs mb-2">{punch.punch_out_adresse}</p>}
                    <div className="flex flex-wrap items-center gap-2">
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        className="h-7 gap-1 text-xs border-red-300 text-red-700 hover:bg-red-50"
                        onClick={() => setMapDialog({ open: true, type: 'out' })}
                        data-testid={`btn-voir-carte-out-${punch.id}`}
                      >
                        <MapIcon className="w-3.5 h-3.5" />
                        Voir sur la carte
                      </Button>
                      <a
                        href={`https://www.google.com/maps?q=${punch.punch_out_latitude},${punch.punch_out_longitude}`}
                        target="_blank" rel="noreferrer"
                        className="inline-flex items-center gap-1 text-[11px] text-gray-500 hover:text-blue-700 hover:underline"
                        data-testid={`gps-link-out-${punch.id}`}
                      >
                        <ExternalLink className="w-3 h-3" />
                        Google Maps ({punch.punch_out_latitude?.toFixed(5)}, {punch.punch_out_longitude?.toFixed(5)})
                      </a>
                    </div>
                  </div>
                );
              })()}
              {!punch.punch_in_latitude && !punch.punch_out_latitude && (
                <p className="text-gray-500 text-center py-4">Aucune donnée GPS disponible pour ce punch.</p>
              )}
            </div>
          </div>
        </div>
      )}
      {/* 🗺️ [Phase v13.41] Dialog carte punch + client
           🆕 [v13.50 — Item 1] Enrichi avec les props punchId + distances routières
           pour permettre le recalcul "Trajet véhicule" à la demande. */}
      <ValidationPunchMapDialog
        open={mapDialog.open}
        onClose={() => setMapDialog({ open: false, type: mapDialog.type })}
        punchType={mapDialog.type}
        punchLat={mapDialog.type === 'out' ? punch.punch_out_latitude : punch.punch_in_latitude}
        punchLng={mapDialog.type === 'out' ? punch.punch_out_longitude : punch.punch_in_longitude}
        punchHeure={formatLocalTime(
          mapDialog.type === 'out' ? punch.punch_out_time : punch.punch_in_time,
          { timeOnly: true }
        )}
        punchAdresse={mapDialog.type === 'out' ? punch.punch_out_adresse : punch.punch_in_adresse}
        employeNom={punch.employee_name || ''}
        clientLat={typeof punch.client_latitude === 'number' ? punch.client_latitude : null}
        clientLng={typeof punch.client_longitude === 'number' ? punch.client_longitude : null}
        clientAdresse={[punch.client_adresse, punch.client_ville].filter(Boolean).join(', ')}
        dossierNom={punch.client_nom || punch.nom_dossier || ''}
        punchId={punch.id}
        distanceRoutiereM={punch.distance_routiere_m ?? null}
        distanceRoutiereDureeMin={punch.distance_routiere_duree_min ?? null}
        distanceRoutiereCalcAt={punch.distance_routiere_calc_at ?? null}
      />
    </div>
  );
}

// ─── Onglet Historique ────────────────────────────────────────────────────────
const ACTION_STYLES = {
  validation: { icon: CheckCircle, color: 'text-green-600', bg: 'bg-green-50', border: 'border-green-200', label: 'Validé' },
  refus:      { icon: XCircle,     color: 'text-red-500',   bg: 'bg-red-50',   border: 'border-red-200',   label: 'Refusé' },
  ajustement: { icon: Edit3,       color: 'text-blue-600',  bg: 'bg-blue-50',  border: 'border-blue-200',  label: 'Ajusté' },
};

function AuditLogTab() {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filterDate, setFilterDate] = useState('');
  const [filterEmployee, setFilterEmployee] = useState('');
  const [filterPeriode, setFilterPeriode] = useState('');
  const [employees, setEmployees] = useState([]);
  // 🆕 [v13.51] Périodes de paie récupérées depuis l'endpoint canonique
  // `/api/paie/periodes` (source unique de vérité — ancre officielle définie
  // dans `backend/routers/contrats.py`). Remplace un ancien calcul JS local
  // qui divergeait de l'ancre entreprise.
  const [periodesPaye, setPeriodesPaye] = useState([]);
  const [periodeCouranteNum, setPeriodeCouranteNum] = useState(null);
  const [page, setPage] = useState(0);
  const PAGE_SIZE = 25;

  useEffect(() => {
    axios.get(`${API_URL}/api/employees`).then(r => r.data).then(d => setEmployees(d.employees || d || [])).catch(() => {});
  }, []);

  // 🆕 [v13.51] Charge les 26 périodes de paie officielles depuis le backend.
  // AUCUN calcul JS local — le frontend consomme uniquement la vérité serveur.
  useEffect(() => {
    axios.get(`${API_URL}/api/paie/periodes?n=26`)
      .then(r => r.data)
      .then(d => {
        setPeriodesPaye(d?.periodes || []);
        setPeriodeCouranteNum(d?.periode_courante?.numero ?? null);
      })
      .catch(() => { /* silencieux — filtre optionnel */ });
  }, []);

  const loadLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: PAGE_SIZE, skip: page * PAGE_SIZE });
      if (filterDate) params.set('date', filterDate);
      if (filterEmployee) params.set('employee_id', filterEmployee);
      if (filterPeriode) params.set('periode', filterPeriode);
      const res = await axios.get(`${API_URL}/api/punch/audit-log?${params}`);
      if (res.status === 200) {
        const data = res.data;
        setLogs(data.logs || []);
        setTotal(data.total || 0);
      }
    } catch { toast.error('Erreur chargement historique'); }
    finally { setLoading(false); }
  }, [page, filterDate, filterEmployee, filterPeriode]);

  useEffect(() => { setPage(0); }, [filterDate, filterEmployee, filterPeriode]);
  useEffect(() => { loadLogs(); }, [loadLogs]);

  // 🆕 [v13.51] Format d'une date ISO en FR-CA compact ("15 févr").
  const fmtDateCourt = (iso) => {
    if (!iso) return '';
    try {
      const d = new Date(iso + 'T12:00:00');
      if (isNaN(d)) return String(iso);
      return d.toLocaleDateString('fr-CA', { day: 'numeric', month: 'short' })
        .replace(/\.$/, '');
    } catch {
      return String(iso);
    }
  };

  // 🆕 [v13.51] Libellé compact d'une période de paie — "P16 · 12 juil au 25 juil".
  // Ajoute "🎯 Actuelle" pour la période courante (cohérence avec RapportPointage).
  const labelPeriode = (p) => {
    if (!p) return '';
    const badge = periodeCouranteNum !== null && p.numero === periodeCouranteNum ? ' 🎯 Actuelle' : '';
    return `P${p.numero} · ${fmtDateCourt(p.debut)} au ${fmtDateCourt(p.fin)}${badge}`;
  };

  const totalPages = Math.ceil(total / PAGE_SIZE);

  return (
    <div className="space-y-4" data-testid="audit-log-tab">
      <div className="flex items-center gap-3 flex-wrap">
        <div>
          <Label className="text-xs text-gray-500">Par employé</Label>
          <select value={filterEmployee} onChange={e => setFilterEmployee(e.target.value)}
            className="border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white w-44" data-testid="audit-log-employee-filter">
            <option value="">Tous les employés</option>
            {employees.filter(e => e.est_actif !== false).sort((a,b) => (a.prenom||'').localeCompare(b.prenom||'')).map(e => (
              <option key={e.id} value={e.id}>{e.prenom} {e.nom}</option>
            ))}
          </select>
        </div>
        <div>
          <Label className="text-xs text-gray-500">Par période de paie</Label>
          <select value={filterPeriode} onChange={e => setFilterPeriode(e.target.value)}
            className="border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white w-52" data-testid="audit-log-periode-filter">
            <option value="">Toutes les périodes</option>
            {periodesPaye.slice().sort((a, b) => b.numero - a.numero).map(p => (
              <option key={p.numero} value={p.debut} data-testid={`audit-log-option-periode-${p.numero}`}>
                {labelPeriode(p)}
              </option>
            ))}
          </select>
        </div>
        <div>
          <Label className="text-xs text-gray-500">Par date</Label>
          <Input type="date" value={filterDate} onChange={e => setFilterDate(e.target.value)} className="w-40" data-testid="audit-log-date-filter" />
        </div>
        {(filterDate || filterEmployee || filterPeriode) && (
          <Button variant="outline" size="sm" className="mt-5" onClick={() => { setFilterDate(''); setFilterEmployee(''); setFilterPeriode(''); }}>
            Réinitialiser
          </Button>
        )}
        <p className="text-sm text-gray-500 mt-5">{total} entrée(s)</p>
      </div>

      {loading ? (
        <div className="text-center py-8 text-gray-400">
          <History className="w-8 h-8 mx-auto mb-2 opacity-30 animate-pulse" />
          <p className="text-sm">Chargement...</p>
        </div>
      ) : logs.length === 0 ? (
        <Card>
          <CardContent className="py-10 text-center text-gray-400">
            <History className="w-10 h-10 mx-auto mb-2 opacity-30" />
            <p className="font-medium">Aucune activité enregistrée</p>
            <p className="text-sm mt-1">Les validations, refus et ajustements apparaîtront ici.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {logs.map(log => {
            const style = ACTION_STYLES[log.action] || ACTION_STYLES.ajustement;
            const Icon = style.icon;
            const logDate = new Date(log.date);
            return (
              <div key={log.id} className={`flex items-start gap-3 px-4 py-3 rounded-lg border ${style.bg} ${style.border}`} data-testid={`audit-entry-${log.id}`}>
                <Icon className={`w-5 h-5 mt-0.5 shrink-0 ${style.color}`} />
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                    <span className={`font-semibold text-sm ${style.color}`}>{style.label}</span>
                    <span className="text-sm text-gray-700">— {log.employee_name || 'Employé inconnu'}</span>
                    {log.type_punch === 'admin' && (
                      <Badge variant="outline" className="text-purple-700 border-purple-300 text-xs">admin</Badge>
                    )}
                  </div>
                  {log.changes && log.changes.length > 0 && (
                    <p className="text-xs text-gray-600 mt-0.5">{log.changes.join(' | ')}</p>
                  )}
                  <p className="text-xs text-gray-400 mt-1">
                    Par <span className="font-medium text-gray-600">{log.actor_name}</span>
                    {' — '}
                    {logDate.toLocaleDateString('fr-CA', { day: 'numeric', month: 'short', year: 'numeric' })}
                    {' à '}
                    {logDate.toLocaleTimeString('fr-CA', { hour: '2-digit', minute: '2-digit' })}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 pt-2">
          <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage(p => p - 1)} data-testid="audit-log-prev">
            Précédent
          </Button>
          <span className="text-sm text-gray-500">Page {page + 1} / {totalPages}</span>
          <Button variant="outline" size="sm" disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)} data-testid="audit-log-next">
            Suivant
          </Button>
        </div>
      )}
    </div>
  );
}

// ─── Page principale ──────────────────────────────────────────────────────────
/**
 * Onglet "Refusés" — audit trail des punches refusés (90 derniers jours).
 * N'apparaît QUE ici, jamais dans les sections employés (MonEspace, Pointage).
 */
export function RefusedPunchesTab() {
  const [punches, setPunches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(90);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API_URL}/api/punch/refused-history?days=${days}&limit=200`)
      .then(r => setPunches(r.data?.punches || []))
      .catch(() => setPunches([]))
      .finally(() => setLoading(false));
  }, [days]);

  const fmtDate = (iso) => iso ? formatLocalTime(iso, { format: 'short' }) : '—';

  if (loading) return <p className="text-center text-slate-400 py-8">Chargement...</p>;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h3 className="text-base font-semibold text-slate-800">Punches refusés</h3>
          <p className="text-xs text-slate-500">
            Audit trail — exclus des stats employés, stats BT et rapports de paie. Visible ici uniquement.
          </p>
        </div>
        <select
          value={days}
          onChange={e => setDays(parseInt(e.target.value))}
          className="text-sm border rounded-md px-2 py-1.5"
          data-testid="refused-days-filter"
        >
          <option value={30}>30 derniers jours</option>
          <option value={90}>90 derniers jours</option>
          <option value={180}>6 derniers mois</option>
          <option value={365}>1 an</option>
        </select>
      </div>

      {punches.length === 0 ? (
        <div className="text-center py-12 text-slate-400 bg-slate-50 rounded-lg border border-dashed">
          <XCircle className="w-8 h-8 mx-auto mb-2 text-slate-300" />
          <p>Aucun punch refusé sur cette période</p>
        </div>
      ) : (
        <div className="space-y-4" data-testid="punches-list-refused">
          {punches.map(p => (
            <div key={p.id} className="border-2 border-red-200 bg-red-50/40 rounded-lg p-4 shadow-sm" data-testid={`refused-punch-${p.id}`}>
              <div className="flex items-start justify-between flex-wrap gap-2">
                <div className="flex-1 min-w-[200px]">
                  <p className="font-semibold text-base text-slate-800">{p.employee_nom}</p>
                  <p className="text-sm text-slate-500 mt-1">
                    {fmtDate(p.punch_in_time)} → {fmtDate(p.punch_out_time)}
                  </p>
                  {p.numero_bt && <p className="text-xs text-slate-600 mt-1">BT : <span className="font-medium">{p.numero_bt}</span></p>}
                  {p.client_nom && <p className="text-xs text-slate-600">Client : {p.client_nom}</p>}
                </div>
                <div className="text-right">
                  <span className="text-xs bg-red-100 text-red-700 font-semibold px-2 py-1 rounded">REFUSÉ</span>
                  {p.heures_nettes > 0 && (
                    <p className="text-[11px] text-slate-500 mt-1">{p.heures_nettes.toFixed(2)}h non comptées</p>
                  )}
                </div>
              </div>
              {p.notes_validation && (
                <div className="mt-2 pt-2 border-t border-red-200">
                  <p className="text-[11px] text-slate-500">Note du valideur :</p>
                  <p className="text-xs text-slate-700 italic">« {p.notes_validation} »</p>
                </div>
              )}
              {p.valide_par_nom && (
                <p className="text-[10px] text-slate-400 mt-1.5">
                  Refusé par {p.valide_par_nom} {p.valide_le && `— ${fmtDate(p.valide_le)}`}
                </p>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export const ValidationHeures = () => {
  const { user, token } = useAuth();
  const isOwner = estProprietaire(user); // Cat H — identité Propriétaire (dispatch UI : Owner voit les outils avancés / config règles pause / actions destructives). RBAC page = `validation_heures` déjà gated par PermissionsContext.
  const { canDo } = usePermissions() || {};
  // 📏 [v13.63 Phase 5] Bloc kilométrage admin par (employé, date). Owner short-circuit,
  //     sinon check strict de la clé matrice.
  const canAjusterKilometrage = !!(isOwner || (canDo && canDo('heures_kilometrage_ajuster')));
  const [loading, setLoading] = useState(true);
  const [punches, setPunches] = useState([]);
  const [selectedDate, setSelectedDate] = useState('');
  const [refreshKey, setRefreshKey] = useState(0);
  const [mainTab, setMainTab] = useState('validation');
  const [subTab, setSubTab] = useState('chantier');
  // ✅ Filtre "Afficher uniquement les punches sans BT" (batch cleanup)
  const [showOnlySansBT, setShowOnlySansBT] = useState(false);
  // 🆕 2026-05-06 Module CCQ — filtre "uniquement les punches CCQ avec grade manquant"
  const [showOnlyCCQGradeMissing, setShowOnlyCCQGradeMissing] = useState(false);

  // Règles de pause
  const [pauseConfig, setPauseConfig] = useState({
    terrain_auto_deduction: true,
    terrain_pause_heures: 0.5,
    admin_manuel: true,
    admin_pause_heures: 0,
    // Règles par rôle admin
    cp_pause_totale: 0.5,
    cp_pause_payee: 0,
    adjoint_pause_totale: 1.0,
    adjoint_pause_payee: 0.5,
    proprio_pause_totale: 0.5,
    proprio_pause_payee: 0.5,
  });
  const [savingPause, setSavingPause] = useState(false);

  // 🆕 [Phase 80.1] State pour le dialog "Test pause PM" (Owner-only)
  const [showTestPausePmDialog, setShowTestPausePmDialog] = useState(false);
  const [creatingTestPunch, setCreatingTestPunch] = useState(false);

  // Import heures
  const [employees, setEmployees] = useState([]);
  const [importForm, setImportForm] = useState({
    employee_id: '', date: '', heure_debut: '', heure_fin: '',
    // 🔁 RÉGRESSION B3 (Eliot, 2026-05-04) : default 0 minutes — l'utilisateur
    // saisit la pause uniquement si l'employé en a effectivement pris une.
    // Pour les admins, JAMAIS de déduction auto.
    type_punch: 'chantier', heures_pause: '0', notes: '', bon_travail_id: '',
    // 🚚 [Phase v13.52 — 2026-07-20] Temps de déplacement en minutes (int).
    temps_deplacement_minutes: '0',
  });
  const [importing, setImporting] = useState(false);
  const [importHistory, setImportHistory] = useState([]);
  // Sélection de dossier pour l'import
  const [dossierSearch, setDossierSearch] = useState('');
  const [dossierResults, setDossierResults] = useState([]);
  const [selectedDossier, setSelectedDossier] = useState(null);
  const [dossieBTs, setDossierBTs] = useState([]);

  useEffect(() => {
    loadData();
    loadPauseConfig();
    // Load employees for import form (inclut les propriétaires sans fiche RH — Éliot etc.)
    axios.get(`${API_URL}/api/employees?actif=true&include_owners=true`).then(r => r.data)
      .then(d => setEmployees(d.employees || []))
      .catch(() => {});
  }, [selectedDate, refreshKey]);

  const loadPauseConfig = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/punch/pause-config`);
      if (res.status === 200) {
        const data = res.data;
        if (data.config) setPauseConfig(data.config);
      }
    } catch (e) {
      console.error('Erreur chargement config pause:', e);
    }
  };

  const savePauseConfig = async () => {
    setSavingPause(true);
    try {
      const res = await axios.post(`${API_URL}/api/punch/pause-config`, pauseConfig);
      if (res.status === 200) toast.success('✅ Règles de pause sauvegardées');
      else toast.error('Erreur sauvegarde');
    } catch { toast.error('Erreur'); }
    finally { setSavingPause(false); }
  };

  // 🆕 [Phase v13.60.17 — 2026-08-18] Sélection multiple pour validation batch
  const [selectedPunchIds, setSelectedPunchIds] = useState(new Set());
  const [showConfirmBatchValidate, setShowConfirmBatchValidate] = useState(false);
  const [batchValidating, setBatchValidating] = useState(false);

  const [bureauCoords, setBureauCoords] = useState(null);

  useEffect(() => {
    loadData();
  }, [selectedDate, refreshKey]);

  const loadData = async () => {
    setLoading(true);
    try {
      const dateParam = selectedDate ? `?date=${selectedDate}` : '';
      const res = await axios.get(`${API_URL}/api/punch/pending-validation${dateParam}`);
      if (res.status === 200) {
        const data = res.data;
        setPunches(data.punches || []);
        setBureauCoords(data.bureau_coords || null);
      }
    } catch { toast.error('Erreur chargement'); }
    finally { setLoading(false); }
  };

  const handleRefresh = () => setRefreshKey(k => k + 1);

  // 🆕 [Phase 80.1 — 2026-06-22] Création d'un punch test pause PM (Owner-only)
  const handleCreateTestPunch = async (scenario, reponseSimulee) => {
    if (creatingTestPunch) return;
    // Si "non" sur le seuil atteint → confirmation supplémentaire (cohérent avec
    // la modale réelle pause PM dans PointageTerrain)
    if (reponseSimulee === 'non') {
      if (!window.confirm(
        "Es-tu sûr de vouloir valider que tu n'as PAS pris ta pause de 15 minutes ?\n\n+15 min payées seront ajoutées au total."
      )) return;
    }
    setCreatingTestPunch(true);
    try {
      const res = await axios.post(`${API_URL}/api/debug/punch-pause-pm-test`, {
        scenario,
        reponse_simulee: reponseSimulee,
      });
      const evaluation = res.data?.punch?.pause_pm_evaluation || {};
      const raison = evaluation.raison_non_admissible;
      const ajout = evaluation.minutes_ajoutees || 0;
      const msg = raison
        ? `🧪 Punch test créé — Scénario ${scenario} → Badge "${raison}" attendu. Persistant — visible dans Rapport d'heures (audit).`
        : `🧪 Punch test créé — Scénario ${scenario}, réponse "${reponseSimulee}" → ${ajout > 0 ? `+${ajout} min ajoutées` : 'aucun ajout'}. Persistant — visible dans Rapport d'heures (audit).`;
      toast.success(msg, { duration: 8000 });
      setShowTestPausePmDialog(false);
      // Recharge la liste pour voir le punch test
      handleRefresh();
      // 🆕 [Phase 80.2] Auto-cleanup 30s retiré → punch persiste pour audit Rapport d'heures
    } catch (err) {
      console.error('[Phase 80.1] handleCreateTestPunch error:', err);
      const detail = err.response?.data?.detail || err.message;
      toast.error(`Erreur création punch test : ${detail}`);
    } finally {
      setCreatingTestPunch(false);
    }
  };

  const punchesModification = punches.filter(p => p.statut === 'modification_soumise');
  // ✅ Punchs "terrain" = tout ce qui n'est pas admin (chantier, interne=BT interne, lavage=route de lavage)
  const punchesChantier = punches.filter(p => (p.type_punch || 'chantier') !== 'admin');
  // 🆕 2026-05-06 Module CCQ — punches CCQ avec grade employé manquant
  const punchesCCQGradeMissing = punchesChantier.filter(
    p => p.bt_regime_ccq === 'ccq' && p.ccq_snapshot?.requires_ccq_grade_review === true
  );
  // ✅ Punches chantier sans BT associé (à corriger dans Validation)
  const punchesSansBT = punchesChantier.filter(p => !p.bon_travail_id);
  const punchesAdmin = punches.filter(p => p.type_punch === 'admin');
  const oubliDepunch = punches.filter(p => !p.punch_out_time);

  // 🆕 Carte { `${emp_id}|${date}|${route_id}` → { totalPauseMin, clientsCount } }
  // Utilisée pour afficher "Total pause jour" sur chaque ligne de route de lavage
  const lavageJourMap = {};
  for (const p of punches) {
    if (p.type_punch !== 'lavage' || !p.route_lavage_id) continue;
    const date = (p.punch_in_time || '').substring(0, 10);
    const key = `${p.employee_id}|${date}|${p.route_lavage_id}`;
    if (!lavageJourMap[key]) lavageJourMap[key] = { totalPauseMin: 0, clientsCount: 0 };
    lavageJourMap[key].totalPauseMin += Math.round(((p.pause_auto_lavage_journee || 0) + (p.heures_pause || 0)) * 60);
    lavageJourMap[key].clientsCount += 1;
  }
  const getLavageJourInfo = (p) => {
    if (p.type_punch !== 'lavage' || !p.route_lavage_id) return null;
    const date = (p.punch_in_time || '').substring(0, 10);
    return lavageJourMap[`${p.employee_id}|${date}|${p.route_lavage_id}`] || null;
  };

  // Recherche de dossier pour l'import
  const handleSearchDossier = async (query) => {
    setDossierSearch(query);
    if (query.length < 2) { setDossierResults([]); return; }
    try {
      const res = await axios.get(`${API_URL}/api/crm/dossiers?search=${encodeURIComponent(query)}&limit=8`);
      setDossierResults(res.data?.dossiers || []);
    } catch { setDossierResults([]); }
  };

  const handleSelectDossier = async (dossier) => {
    setSelectedDossier(dossier);
    setDossierSearch(dossier.client_nom || dossier.nom_dossier || '');
    setDossierResults([]);
    // Charger les BTs du dossier
    try {
      const res = await axios.get(`${API_URL}/api/crm/bons-travail?dossier_id=${dossier.id}`);
      setDossierBTs(res.data?.bons_travail || []);
    } catch { setDossierBTs([]); }
  };

  const handleImportPunch = async () => {
    if (!importForm.employee_id || !importForm.date || !importForm.heure_debut || !importForm.heure_fin) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }
    setImporting(true);
    try {
      // Convertir les minutes en heures décimales
      const pauseMinutes = parseFloat(importForm.heures_pause) || 0;
      const pauseHeures = pauseMinutes / 60;
      
      const res = await axios.post(`${API_URL}/api/punch/import-manual`, {
        ...importForm,
        heures_pause: pauseHeures,
        // 🚚 [Phase v13.52 — 2026-07-20] Cast en int (backend clampe si négatif).
        temps_deplacement_minutes: Math.max(0, parseInt(importForm.temps_deplacement_minutes, 10) || 0),
      });
      const data = res.data;
      if (res.status === 200) {
        toast.success(data.message || 'Punch importé avec succès');
        setImportHistory(prev => [data.punch, ...prev]);
        // Reset form but keep employee and type
        setImportForm(f => ({
          ...f, date: '', heure_debut: '', heure_fin: '', notes: '', bon_travail_id: '',
          temps_deplacement_minutes: '0',
        }));
        setSelectedDossier(null);
        setDossierSearch('');
        setDossierBTs([]);
      } else {
        toast.error(data.detail || 'Erreur import');
      }
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur réseau'); }
    finally { setImporting(false); }
  };


  // 🆕 [Phase v13.60.17 — 2026-08-18] Toggle sélection d'un punch
  const togglePunchSelection = useCallback((punchId) => {
    setSelectedPunchIds(prev => {
      const next = new Set(prev);
      if (next.has(punchId)) next.delete(punchId);
      else next.add(punchId);
      return next;
    });
  }, []);

  // 🆕 [Phase v13.60.17] Master toggle : sélectionne / désélectionne tous les punches VALIDABLES
  //   visibles fournis en argument. Exclut les punches sans punch_out_time (cohérent
  //   avec handleValidateAll). Un clic bascule entre "tout / rien".
  const toggleAllVisibleSelection = useCallback((visiblePunchIds) => {
    setSelectedPunchIds(prev => {
      const allAlreadySelected = visiblePunchIds.length > 0
        && visiblePunchIds.every(id => prev.has(id));
      if (allAlreadySelected) {
        // Décoche : retire les visibles de la sélection
        const next = new Set(prev);
        visiblePunchIds.forEach(id => next.delete(id));
        return next;
      }
      // Coche : ajoute tous les visibles à la sélection
      const next = new Set(prev);
      visiblePunchIds.forEach(id => next.add(id));
      return next;
    });
  }, []);

  const clearSelection = useCallback(() => setSelectedPunchIds(new Set()), []);

  // 🆕 [Phase v13.60.17] Validation batch des punches sélectionnés.
  //   Boucle côté client sur PUT /api/punch/{id}/adjust (endpoint déjà en place).
  //   Note : si le nombre de punches sélectionnés dépasse régulièrement 50+, envisager
  //   la création d'un endpoint batch dédié POST /api/punch/validate-batch en v13.61.
  const handleValidateSelection = async () => {
    const ids = Array.from(selectedPunchIds);
    if (ids.length === 0) {
      toast.error('Aucun punch sélectionné');
      setShowConfirmBatchValidate(false);
      return;
    }
    const token = localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token');
    if (!token) {
      toast.error('Session expirée — reconnectez-vous');
      setShowConfirmBatchValidate(false);
      return;
    }
    setBatchValidating(true);
    let ok = 0;
    const errors = [];
    for (const id of ids) {
      try {
        const res = await axios.put(
          `${API_URL}/api/punch/${id}/adjust`,
          { valide: true },
          { headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` } }
        );
        if (res.status === 200) ok++;
        else errors.push({ id, msg: res.data?.detail || `Erreur ${res.status}` });
      } catch (e) {
        errors.push({ id, msg: e.response?.data?.detail || 'Erreur réseau' });
      }
    }
    setBatchValidating(false);
    setShowConfirmBatchValidate(false);
    setSelectedPunchIds(new Set());
    if (ok > 0) toast.success(`${ok} pointage(s) validé(s)`);
    if (errors.length > 0) {
      console.warn('[BATCH_VALIDATE] Erreurs:', errors);
      toast.error(`${errors.length} échec(s) — voir la console pour le détail`);
    }
    handleRefresh();
  };

  const handleValidateAll = async (type) => {
    const toValidate = (type === 'admin' ? punchesAdmin : punchesChantier).filter(p => p.punch_out_time);
    if (toValidate.length === 0) { toast.error('Aucun punch complet à valider'); return; }
    if (!window.confirm(`Valider les ${toValidate.length} pointage(s) sélectionnés?`)) return;
    const token = localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token');
    if (!token) { toast.error('Session expirée — reconnectez-vous'); return; }
    let ok = 0;
    let lastError = '';
    for (const p of toValidate) {
      try {
        const res = await axios.put(`${API_URL}/api/punch/${p.id}/adjust`, 
          { valide: true },
          { headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` } }
        );
        if (res.status === 200) ok++;
        else {
          lastError = res.data?.detail || `Erreur ${res.status}`;
        }
      } catch (e) { 
        lastError = e.response?.data?.detail || 'Erreur réseau'; 
      }
    }
    if (ok > 0) toast.success(`${ok} pointage(s) validé(s)`);
    if (lastError) toast.error(`Certains pointages n'ont pas pu être validés: ${lastError}`);
    handleRefresh();
  };

  return (
    <div className="p-4 sm:p-6 max-w-5xl mx-auto space-y-6 pb-24">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 flex items-center gap-3">
            <Clock className="w-7 h-7 text-blue-600" />
            Validation des heures
          </h1>
          <p className="text-gray-500 mt-1 text-sm">
            Vérifiez, ajustez et validez les pointages.
          </p>
        </div>
      </div>

      {/* Navigation principale */}
      <Tabs value={mainTab} onValueChange={setMainTab}>
        <TabsList className="w-full max-w-lg flex flex-wrap h-auto">
          <TabsTrigger value="validation" className="flex-1">
            <Clock className="w-4 h-4 mr-1" /> Validation ({punches.length})
          </TabsTrigger>
          <TabsTrigger value="import" className="flex-1" data-testid="tab-import">
            <Plus className="w-4 h-4 mr-1" /> Importer des heures
          </TabsTrigger>
          <TabsTrigger value="historique" className="flex-1" data-testid="tab-historique">
            <History className="w-4 h-4 mr-1" /> Historique
          </TabsTrigger>
          <TabsTrigger value="refuses" className="flex-1" data-testid="tab-refuses">
            <XCircle className="w-4 h-4 mr-1" /> Refusés
          </TabsTrigger>
          <TabsTrigger value="regles-pause" className="flex-1">
            <Coffee className="w-4 h-4 mr-1" /> Règles de pause
          </TabsTrigger>
        </TabsList>

        {/* ═══ ONGLET VALIDATION ═══ */}
        <TabsContent value="validation" className="mt-4 space-y-4">
          {/* ✅ 2026-04-27 : Bannière de rappel des règles de pause par rôle */}
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-xs" data-testid="banner-regles-pause">
            <p className="font-semibold text-slate-700 mb-1.5 flex items-center gap-1.5">
              <Coffee className="w-3.5 h-3.5 text-amber-600" /> Règles de pause appliquées (par rôle)
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
              <div className="bg-white border-l-4 border-blue-400 px-2 py-1.5 rounded">
                <p className="font-semibold text-blue-700">Terrain (peintres, chefs)</p>
                <p className="text-slate-600">30 min forcées si {">"} 6h · <span className="text-red-600 font-semibold">0 payée</span></p>
              </div>
              <div className="bg-white border-l-4 border-purple-400 px-2 py-1.5 rounded">
                <p className="font-semibold text-purple-700">Chargé de projet (CP)</p>
                <p className="text-slate-600">Pause à la minute · <span className="text-red-600 font-semibold">Tout déduit</span></p>
              </div>
              <div className="bg-white border-l-4 border-amber-400 px-2 py-1.5 rounded">
                <p className="font-semibold text-amber-700">Adjoint</p>
                <p className="text-slate-600"><span className="text-emerald-600 font-semibold">30 min payées</span> · reste déduit à la min</p>
              </div>
              <div className="bg-white border-l-4 border-emerald-400 px-2 py-1.5 rounded">
                <p className="font-semibold text-emerald-700">Propriétaire</p>
                <p className="text-slate-600"><span className="text-emerald-600 font-semibold">30 min payées</span> · reste déduit</p>
              </div>
            </div>
          </div>

          {/* Filtre date */}
          <div className="flex items-center gap-2 flex-wrap">
            <div>
              <Label className="text-xs text-gray-500">Date</Label>
              <Input type="date" value={selectedDate} onChange={e => setSelectedDate(e.target.value)} className="w-40" />
            </div>
            <Button 
              variant={!selectedDate ? "default" : "outline"} 
              onClick={() => setSelectedDate('')} 
              className="mt-5"
              data-testid="btn-toutes-dates"
            >
              Toutes les dates
            </Button>
            <Button variant="outline" onClick={handleRefresh} className="mt-5">
              <RefreshCw className="w-4 h-4" />
            </Button>
            {/* 🆕 [Phase 80.1+80.2 — 2026-06-22] Bouton test pause PM (Owner-only)
                Crée un punch fictif (is_test_punch=true) qui apparaît dans la
                liste avec un badge "🧪 TEST" pour valider le flow pause PM
                en bout-en-bout. Phase 80.2 : punch persiste pour audit Rapport
                d'heures, suppression manuelle via Rapport d'heures ou DELETE. */}
            {isOwner && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowTestPausePmDialog(true)}
                className="mt-5 border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                data-testid="btn-test-pause-pm"
                title="Crée un punch fictif pour tester le flow pause PM (persistant pour audit)"
              >
                🧪 Test pause PM
              </Button>
            )}
          </div>

          {/* Stats résumé */}
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-center">
              <p className="text-2xl font-bold text-blue-700">{punches.length}</p>
              <p className="text-xs text-blue-600">Total à valider</p>
            </div>
            <div className={`border rounded-lg p-3 text-center ${punchesModification.length > 0 ? 'bg-orange-100 border-orange-400 ring-1 ring-orange-300' : 'bg-orange-50 border-orange-200'}`}>
              <p className="text-2xl font-bold text-orange-700">{punchesModification.length}</p>
              <p className="text-xs text-orange-600 font-semibold">Modifications soumises</p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-center">
              <p className="text-2xl font-bold text-amber-700">{oubliDepunch.length}</p>
              <p className="text-xs text-amber-600">Oubli de punch OUT</p>
            </div>
            <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-center">
              <p className="text-2xl font-bold text-green-700">{punchesChantier.length}</p>
              <p className="text-xs text-green-600">Pointages chantier</p>
            </div>
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 text-center">
              <p className="text-2xl font-bold text-purple-700">{punchesAdmin.length}</p>
              <p className="text-xs text-purple-600">Pointages admin</p>
            </div>
          </div>

          {/* Alerte modifications soumises */}
          {punchesSansBT.length > 0 && (
            <div className="flex items-start gap-2 bg-red-50 border border-red-400 rounded-lg p-3" data-testid="alerte-sans-bt">
              <AlertTriangle className="w-5 h-5 text-red-600 shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-red-800">
                  {punchesSansBT.length} pointage(s) sans bon de travail associé
                </p>
                <p className="text-xs text-red-700 mt-0.5">
                  Ces heures ne sont liées à aucun chantier — elles ne seront pas comptées dans les statistiques par projet. Ouvrez le crayon sur chaque ligne pour associer un BT (recherche par client ou numéro).
                </p>
              </div>
            </div>
          )}

          {/* 🆕 2026-05-06 Module CCQ — Bannière "punchs CCQ sans grade" */}
          {punchesCCQGradeMissing.length > 0 && (
            <button
              type="button"
              onClick={() => {
                setShowOnlyCCQGradeMissing(prev => !prev);
                if (showOnlySansBT) setShowOnlySansBT(false);
                if (subTab !== 'chantier') setSubTab('chantier');
              }}
              className={`w-full flex items-start gap-2 rounded-lg p-3 text-left transition ${
                showOnlyCCQGradeMissing
                  ? 'bg-amber-100 border-2 border-amber-500 shadow-sm'
                  : 'bg-amber-50 border border-amber-400 hover:bg-amber-100'
              }`}
              data-testid="banner-ccq-grade-missing"
            >
              <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-semibold text-amber-900 flex items-center gap-2">
                  ⚠️ {punchesCCQGradeMissing.length} punch(s) sur BT CCQ requièrent un grade
                  {showOnlyCCQGradeMissing && (
                    <span className="text-xs bg-amber-600 text-white px-2 py-0.5 rounded-full ml-2">FILTRE ACTIF</span>
                  )}
                </p>
                <p className="text-xs text-amber-800 mt-0.5">
                  {showOnlyCCQGradeMissing
                    ? 'Cliquer pour réafficher tous les punchs.'
                    : 'Cliquer pour filtrer la liste et corriger ces punchs (ajouter un grade CCQ à l\'employé concerné dans Gestion des employés).'}
                </p>
              </div>
            </button>
          )}

          {/* Alerte modifications soumises */}
          {punchesModification.length > 0 && (
            <div className="flex items-start gap-2 bg-orange-50 border border-orange-400 rounded-lg p-3" data-testid="alerte-modifications">
              <AlertTriangle className="w-5 h-5 text-orange-600 shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-orange-800">
                  {punchesModification.length} modification(s) soumise(s) par des employés
                </p>
                <p className="text-xs text-orange-700 mt-0.5">
                  Ces employés ont soumis une demande de correction sur leur pointage. Veuillez vérifier et réajuster si nécessaire.
                </p>
              </div>
            </div>
          )}

          {oubliDepunch.length > 0 && (
            <div className="flex items-start gap-2 bg-orange-50 border border-orange-300 rounded-lg p-3">
              <AlertTriangle className="w-5 h-5 text-orange-500 shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-orange-800">
                  {oubliDepunch.length} employé(s) ont oublié de se dépuncher
                </p>
                <p className="text-xs text-orange-700 mt-0.5">
                  Cliquez sur l'icône ✏️ pour corriger l'heure de punch OUT avant de valider.
                </p>
              </div>
            </div>
          )}

          {loading ? (
            <div className="text-center py-12 text-gray-400">
              <Clock className="w-12 h-12 mx-auto mb-3 opacity-30 animate-pulse" />
              <p>Chargement des pointages...</p>
            </div>
          ) : punches.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center text-gray-400">
                <CheckCircle className="w-12 h-12 mx-auto mb-3 opacity-30 text-green-500" />
                <p className="font-medium text-green-700">Aucun pointage en attente de validation pour cette date!</p>
                <p className="text-sm mt-1">Essayez une autre date ou tous les pointages ont déjà été traités.</p>
              </CardContent>
            </Card>
          ) : (
            <Tabs value={subTab} onValueChange={setSubTab}>
              <TabsList className="w-full max-w-sm">
                <TabsTrigger value="chantier" className="flex-1">
                  Chantier ({punchesChantier.length})
                </TabsTrigger>
                <TabsTrigger value="admin" className="flex-1">
                  Admin ({punchesAdmin.length})
                </TabsTrigger>
              </TabsList>

              <TabsContent value="chantier" className="mt-4 space-y-4">
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2">
                  <div className="flex items-center gap-3 flex-wrap">
                    <p className="text-sm text-gray-500">
                      Les heures validées seront intégrées dans les bons de travail correspondants.
                    </p>
                    {/* ✅ Filtre : Afficher uniquement les pointages sans BT (pour nettoyage rapide) */}
                    {punchesSansBT.length > 0 && (
                      <label className="flex items-center gap-2 cursor-pointer bg-red-50 border border-red-300 rounded-lg px-3 py-1.5 text-xs font-medium text-red-700 hover:bg-red-100" data-testid="filter-sans-bt" title="Masquer les punches déjà associés à un BT pour ne traiter que ceux qui doivent encore être corrigés">
                        <input
                          type="checkbox"
                          checked={showOnlySansBT}
                          onChange={e => setShowOnlySansBT(e.target.checked)}
                          className="accent-red-600"
                        />
                        Afficher seulement les {punchesSansBT.length} pointage(s) sans BT
                      </label>
                    )}
                  </div>
                  <Button size="sm" className="bg-green-600 hover:bg-green-700 w-full sm:w-auto" onClick={() => handleValidateAll('chantier')}>
                    <CheckCircle className="w-4 h-4 mr-1" /> Valider tout ({punchesChantier.filter(p => p.punch_out_time).length})
                  </Button>
                </div>
                {(() => {
                  let listeAffichee = punchesChantier;
                  if (showOnlySansBT) listeAffichee = punchesSansBT;
                  else if (showOnlyCCQGradeMissing) listeAffichee = punchesCCQGradeMissing;
                  if (listeAffichee.length === 0) {
                    return (
                      <p className="text-center text-gray-400 py-6">
                        {showOnlySansBT ? 'Aucun pointage sans BT ✓'
                          : showOnlyCCQGradeMissing ? 'Aucun punch CCQ sans grade ✓'
                          : 'Aucun pointage chantier à valider'}
                      </p>
                    );
                  }
                  // 🆕 [Phase v13.60.17] IDs des punches VALIDABLES visibles (exclut les incomplets)
                  const visibleValidableIds = listeAffichee
                    .filter(p => p.punch_out_time)
                    .map(p => p.id);
                  const allVisibleSelected = visibleValidableIds.length > 0
                    && visibleValidableIds.every(id => selectedPunchIds.has(id));
                  return (
                    <div className="space-y-4" data-testid="punches-list-pending">
                      {/* 🆕 [Phase v13.60.17 — 2026-08-18] Master checkbox "Tout sélectionner (visibles)".
                          Sélectionne uniquement les punches actuellement affichés (respecte les filtres actifs).
                          Recliquer décoche tout. */}
                      {visibleValidableIds.length > 0 && (
                        <div className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
                          <Checkbox
                            checked={allVisibleSelected}
                            onCheckedChange={() => toggleAllVisibleSelection(visibleValidableIds)}
                            aria-label="Tout sélectionner (visibles)"
                            data-testid="checkbox-select-all-visible-chantier"
                          />
                          <span className="text-xs text-slate-600 font-medium">
                            Tout sélectionner ({visibleValidableIds.length} punch{visibleValidableIds.length > 1 ? 's' : ''} validable{visibleValidableIds.length > 1 ? 's' : ''} visible{visibleValidableIds.length > 1 ? 's' : ''})
                          </span>
                        </div>
                      )}
                      {/* 🆕 [Phase 42 #5] Tri groupé : par date, puis par employé,
                          puis par heure. Punches d'un même employé se collent. */}
                      {[...listeAffichee]
                        .sort((a, b) => {
                          const dA = (a.punch_in_time || '').slice(0, 10);
                          const dB = (b.punch_in_time || '').slice(0, 10);
                          if (dA !== dB) return dB.localeCompare(dA); // date desc
                          const eA = (a.employee_nom || '').toLowerCase();
                          const eB = (b.employee_nom || '').toLowerCase();
                          if (eA !== eB) return eA.localeCompare(eB);
                          return (a.punch_in_time || '').localeCompare(b.punch_in_time || '');
                        })
                        .map((p, idx, arr) => {
                          const prev = arr[idx - 1];
                          const sameEmp = prev && prev.employee_id === p.employee_id
                            && (prev.punch_in_time || '').slice(0, 10) === (p.punch_in_time || '').slice(0, 10);
                          const dateIso = (p.punch_in_time || '').slice(0, 10);
                          return (
                            <div key={`${p.id}-${idx}`}
                                 className={sameEmp ? "ml-4 pl-2 border-l-2 border-slate-200" : ""}
                                 data-testid={sameEmp ? `same-emp-row-${p.id}` : `new-emp-row-${p.id}`}>
                              {/* 📏 [v13.63 Phase 5] Bloc kilométrage — 1× par (employé, date) au tout début du groupe */}
                              {!sameEmp && p.employee_id && dateIso && (
                                <div className="mb-2" data-testid={`km-block-wrapper-${p.employee_id}-${dateIso}`}>
                                  <KilometrageAdminBlock
                                    employeeId={p.employee_id}
                                    dateIso={dateIso}
                                    token={token}
                                    canAjuster={canAjusterKilometrage}
                                  />
                                </div>
                              )}
                              <PunchRow
                                punch={p}
                                onUpdate={handleRefresh}
                                isOwner={isOwner}
                                lavageJourInfo={getLavageJourInfo(p)}
                                bureauCoords={bureauCoords}
                                isSelected={selectedPunchIds.has(p.id)}
                                onToggleSelect={togglePunchSelection}
                              />
                            </div>
                          );
                        })}
                    </div>
                  );
                })()}
              </TabsContent>

              <TabsContent value="admin" className="mt-4 space-y-4">
                <div className="flex justify-between items-center">
                  <p className="text-sm text-gray-500">
                    Les pointages admin ne sont <strong>pas</strong> liés à un chantier. Ils vont uniquement dans la page Pointage.
                  </p>
                  {isOwner ? (
                    <Button size="sm" className="bg-purple-600 hover:bg-purple-700" onClick={() => handleValidateAll('admin')}>
                      <CheckCircle className="w-4 h-4 mr-1" /> Valider tout admin ({punchesAdmin.filter(p => p.punch_out_time).length})
                    </Button>
                  ) : (
                    <span className="text-xs text-gray-400 italic">Approbation réservée au propriétaire</span>
                  )}
                </div>
                {punchesAdmin.length === 0 ? (
                  <p className="text-center text-gray-400 py-6">Aucun pointage admin à valider</p>
                ) : (() => {
                  // 🆕 [Phase v13.60.17] Master checkbox pour l'onglet Admin (Owner uniquement)
                  const visibleValidableAdminIds = isOwner
                    ? punchesAdmin.filter(p => p.punch_out_time).map(p => p.id)
                    : [];
                  const allAdminVisibleSelected = visibleValidableAdminIds.length > 0
                    && visibleValidableAdminIds.every(id => selectedPunchIds.has(id));
                  return (
                    <div className="space-y-4" data-testid="punches-list-admin">
                      {isOwner && visibleValidableAdminIds.length > 0 && (
                        <div className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
                          <Checkbox
                            checked={allAdminVisibleSelected}
                            onCheckedChange={() => toggleAllVisibleSelection(visibleValidableAdminIds)}
                            aria-label="Tout sélectionner (admin visibles)"
                            data-testid="checkbox-select-all-visible-admin"
                          />
                          <span className="text-xs text-slate-600 font-medium">
                            Tout sélectionner ({visibleValidableAdminIds.length} punch{visibleValidableAdminIds.length > 1 ? 's' : ''} admin validable{visibleValidableAdminIds.length > 1 ? 's' : ''})
                          </span>
                        </div>
                      )}
                      {punchesAdmin.map((p, idx) => (
                        <PunchRow
                          key={`admin-${p.id}-${idx}`}
                          punch={p}
                          onUpdate={handleRefresh}
                          adminLocked={!isOwner}
                          isOwner={isOwner}
                          lavageJourInfo={getLavageJourInfo(p)}
                          bureauCoords={bureauCoords}
                          isSelected={selectedPunchIds.has(p.id)}
                          onToggleSelect={togglePunchSelection}
                        />
                      ))}
                    </div>
                  );
                })()}
              </TabsContent>
            </Tabs>
          )}
        </TabsContent>


        {/* ═══ ONGLET IMPORT ═══ */}
        <TabsContent value="import" className="mt-4 space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Plus className="w-4 h-4 text-blue-600" />
                Importer des heures manuellement
              </CardTitle>
              <p className="text-sm text-slate-500 mt-1">
                Créez un pointage validé directement. Utile pour importer les heures de l'ancienne plateforme ou corriger un punch manquant.
              </p>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label>Employé *</Label>
                  <select
                    value={importForm.employee_id}
                    onChange={e => setImportForm(f => ({ ...f, employee_id: e.target.value }))}
                    className="w-full mt-1 border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                    data-testid="import-employee"
                  >
                    <option value="">Sélectionner un employé...</option>
                    {employees.sort((a, b) => (a.prenom || '').localeCompare(b.prenom || '')).map(e => (
                      <option key={e.id} value={e.id}>{e.prenom} {e.nom}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label>Date *</Label>
                  <Input
                    type="date"
                    value={importForm.date}
                    onChange={e => setImportForm(f => ({ ...f, date: e.target.value }))}
                    className="mt-1"
                    data-testid="import-date"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <div>
                  <Label>Heure début *</Label>
                  <Input
                    type="time"
                    value={importForm.heure_debut}
                    onChange={e => setImportForm(f => ({ ...f, heure_debut: e.target.value }))}
                    className="mt-1"
                    data-testid="import-heure-debut"
                  />
                </div>
                <div>
                  <Label>Heure fin *</Label>
                  <Input
                    type="time"
                    value={importForm.heure_fin}
                    onChange={e => setImportForm(f => ({ ...f, heure_fin: e.target.value }))}
                    className="mt-1"
                    data-testid="import-heure-fin"
                  />
                </div>
                <div>
                  <Label>Type</Label>
                  <select
                    value={importForm.type_punch}
                    onChange={e => setImportForm(f => ({ ...f, type_punch: e.target.value }))}
                    className="w-full mt-1 border rounded-md px-3 py-2 text-sm"
                    data-testid="import-type"
                  >
                    <option value="chantier">Chantier</option>
                    <option value="admin">Admin / Bureau</option>
                  </select>
                </div>
                <div>
                  <Label>Pause (minutes)</Label>
                  <Input
                    type="number"
                    min="0"
                    max="120"
                    placeholder="Ex: 30"
                    value={importForm.heures_pause}
                    onChange={e => setImportForm(f => ({ ...f, heures_pause: e.target.value }))}
                    className="mt-1"
                    data-testid="import-pause"
                  />
                  <p className="text-xs text-slate-500 mt-1">En minutes (ex: 30 min, 45 min, 32 min...)</p>
                </div>
              </div>

              {/* 🚚 [Phase v13.52 — 2026-07-20] Temps de déplacement à l'import. */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="flex items-center gap-1">
                    Temps déplacement (min)
                    <span className="text-[10px] text-slate-400">Compte comme heures régulières</span>
                  </Label>
                  <Input
                    type="number"
                    min="0"
                    placeholder="Ex: 45"
                    value={importForm.temps_deplacement_minutes}
                    onChange={e => setImportForm(f => ({ ...f, temps_deplacement_minutes: e.target.value }))}
                    className="mt-1"
                    data-testid="import-temps-deplacement"
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    = {((parseInt(importForm.temps_deplacement_minutes, 10) || 0) / 60).toFixed(2)}h ajoutées aux heures nettes
                  </p>
                </div>
              </div>

              <div>
                <Label>Notes (optionnel)</Label>
                <Input
                  value={importForm.notes}
                  onChange={e => setImportForm(f => ({ ...f, notes: e.target.value }))}
                  placeholder="Ex: Import depuis ancienne plateforme, correction punch manquant..."
                  className="mt-1"
                  data-testid="import-notes"
                />
              </div>

              {/* Lier au dossier client (optionnel) */}
              <div className="border-t pt-4">
                <Label className="flex items-center gap-1">
                  Bon de travail (optionnel)
                  <span className="text-xs text-slate-400 font-normal ml-1">— Pour connecter à la rentabilité du projet</span>
                </Label>
                {/* 🆕 2026-05-08 — Composant unifié BtSearchCombobox (logique cohérente avec
                    Rapport d'heures et Validation des heures). Recherche live globale par
                    client, projet, dossier, adresse ou n° BT. */}
                <div className="mt-1">
                  <BtSearchCombobox
                    value={importForm.bon_travail_id}
                    onChange={(id, bt) => {
                      setImportForm(f => ({ ...f, bon_travail_id: id || '' }));
                      // Garder l'info dossier pour affichage du chip vert
                      if (bt) {
                        setSelectedDossier({
                          id: bt.dossier_id,
                          client_nom: bt.client_nom,
                          nom_dossier: bt.nom_dossier,
                        });
                      } else {
                        setSelectedDossier(null);
                      }
                    }}
                    referenceDate={importForm.date || undefined}
                    placeholder="🔍 Rechercher par client, projet, dossier, adresse ou n° BT…"
                    testIdPrefix="import-bt"
                  />
                </div>
              </div>

              {/* Preview */}
              {importForm.heure_debut && importForm.heure_fin && (() => {
                try {
                  const t1 = new Date(`2000-01-01T${importForm.heure_debut}`);
                  const t2 = new Date(`2000-01-01T${importForm.heure_fin}`);
                  const brut = (t2 - t1) / 3600000;
                  const pauseMinutes = parseFloat(importForm.heures_pause) || 0;
                  const pause = pauseMinutes / 60; // Convertir en heures
                  const net = Math.max(0, brut - pause);
                  return (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-center gap-4">
                      <div className="text-center">
                        <p className="text-xs text-blue-600">Brut</p>
                        <p className="text-sm font-bold text-blue-800">{brut.toFixed(1)}h</p>
                      </div>
                      <span className="text-blue-400">−</span>
                      <div className="text-center">
                        <p className="text-xs text-blue-600">Pause</p>
                        <p className="text-sm font-bold text-blue-800">{pauseMinutes}min ({pause.toFixed(2)}h)</p>
                      </div>
                      <span className="text-blue-400">=</span>
                      <div className="text-center">
                        <p className="text-xs text-blue-600">Net</p>
                        <p className="text-lg font-bold text-blue-800">{net.toFixed(1)}h</p>
                      </div>
                    </div>
                  );
                } catch { return null; }
              })()}

              <Button
                onClick={handleImportPunch}
                disabled={importing || !importForm.employee_id || !importForm.date || !importForm.heure_debut || !importForm.heure_fin}
                className="bg-blue-600 hover:bg-blue-700"
                data-testid="btn-import-punch"
              >
                {importing ? <><Loader className="w-4 h-4 mr-2 animate-spin" /> Import en cours...</> : <><Plus className="w-4 h-4 mr-2" /> Créer le pointage</>}
              </Button>
            </CardContent>
          </Card>

          {/* Historique des imports de cette session */}
          {importHistory.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-sm">Pointages importés cette session ({importHistory.length})</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-1">
                  {importHistory.map(p => (
                    <div key={p.id} className="flex items-center justify-between text-sm p-2 bg-green-50 border border-green-200 rounded">
                      <span className="font-medium text-green-800">{p.employee_nom}</span>
                      <span className="text-green-600">{p.punch_in_time?.split('T')[0]}</span>
                      <span className="text-green-600">
                        {p.punch_in_time?.split('T')[1]?.substring(0, 5)} → {p.punch_out_time?.split('T')[1]?.substring(0, 5)}
                      </span>
                      <Badge className="bg-green-100 text-green-700">{p.heures_nettes}h</Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>


        {/* ═══ ONGLET HISTORIQUE ═══ */}
        <TabsContent value="historique" className="mt-4 space-y-4">
          <AuditLogTab />
        </TabsContent>

        {/* ═══ ONGLET PUNCHES REFUSÉS (audit trail) ═══ */}
        <TabsContent value="refuses" className="mt-4 space-y-4">
          <RefusedPunchesTab />
        </TabsContent>

        {/* ═══ ONGLET RÈGLES DE PAUSE ═══ */}
        <TabsContent value="regles-pause" className="mt-4 space-y-4">
          <Card className="bg-amber-50 border-amber-200">
            <CardContent className="pt-4 pb-3">
              <div className="flex items-start gap-3">
                <Coffee className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
                <div className="text-sm text-amber-800 space-y-1">
                  <p className="font-semibold">Comment fonctionnent les règles de pause ?</p>
                  <ul className="list-disc list-inside text-amber-700 space-y-0.5">
                    <li><strong>Terrain :</strong> La pause est déduite automatiquement à chaque pointage (montant configurable)</li>
                    <li><strong>Admin :</strong> Les employés se dépunchent manuellement pour leur pause — aucune déduction automatique</li>
                    <li>Ces valeurs servent de défaut dans la page "Validation des heures" (modifiables par punch)</li>
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Terrain */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Building2 className="w-4 h-4 text-green-600" /> Employés Terrain (Chantier)
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium">Déduction automatique</p>
                    <p className="text-xs text-slate-500">Pause retirée automatiquement à chaque punch</p>
                  </div>
                  <Switch
                    checked={pauseConfig.terrain_auto_deduction}
                    onCheckedChange={v => setPauseConfig(c => ({ ...c, terrain_auto_deduction: v }))}
                  />
                </div>
                {pauseConfig.terrain_auto_deduction && (
                  <div>
                    <Label>Durée de la pause</Label>
                    <div className="flex items-center gap-2 mt-1">
                      <Input
                        type="number"
                        step="0.25"
                        min="0"
                        max="2"
                        value={pauseConfig.terrain_pause_heures}
                        onChange={e => setPauseConfig(c => ({ ...c, terrain_pause_heures: parseFloat(e.target.value) || 0 }))}
                        className="w-24"
                      />
                      <span className="text-sm text-slate-600">heure(s)</span>
                      <Badge variant="outline" className="text-xs">
                        {pauseConfig.terrain_pause_heures * 60} min
                      </Badge>
                    </div>
                    <p className="text-xs text-slate-400 mt-1">Exemples: 0.5 = 30 min, 0.75 = 45 min</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* CP */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <User className="w-4 h-4 text-purple-600" /> Chargée de projet (CP)
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <Label className="text-xs">Pause totale recommandée (heures)</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <Input type="number" step="0.25" min="0" max="2" value={pauseConfig.cp_pause_totale}
                      onChange={e => setPauseConfig(c => ({ ...c, cp_pause_totale: parseFloat(e.target.value) || 0 }))} className="w-24" />
                    <Badge variant="outline" className="text-xs">{(pauseConfig.cp_pause_totale * 60).toFixed(0)} min</Badge>
                  </div>
                  <p className="text-xs text-slate-500 mt-0.5">Durée "normale" de pause (utilisée si travail &gt; 6h)</p>
                </div>
                <div>
                  <Label className="text-xs">Pause payée (maximum)</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <Input type="number" step="0.25" min="0" max="2" value={pauseConfig.cp_pause_payee}
                      onChange={e => setPauseConfig(c => ({ ...c, cp_pause_payee: parseFloat(e.target.value) || 0 }))} className="w-24" />
                    <Badge variant="outline" className="text-xs">{(pauseConfig.cp_pause_payee * 60).toFixed(0)} min payée</Badge>
                  </div>
                </div>
                <div className="bg-purple-50 border border-purple-200 rounded p-2 text-xs">
                  <p className="font-semibold text-purple-700 mb-1">📌 Règle de déduction :</p>
                  <p className="text-purple-600">• Pause payée : <strong>maximum {(pauseConfig.cp_pause_payee * 60).toFixed(0)} min</strong></p>
                  <p className="text-purple-600">• Pause non payée : <strong>TOUT le reste est déduit</strong></p>
                  <p className="text-purple-700 mt-1"><strong>Exemples :</strong></p>
                  <p className="text-purple-600">  - Prend 20 min → enlève 20 min</p>
                  <p className="text-purple-600">  - Prend 45 min → enlève 45 min</p>
                  <p className="text-purple-600">  - Prend 1h → enlève 1h</p>
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Adjoint Admin */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <User className="w-4 h-4 text-blue-600" /> Adjoint administratif
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <Label className="text-xs">Pause totale recommandée (heures)</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <Input type="number" step="0.25" min="0" max="2" value={pauseConfig.adjoint_pause_totale}
                      onChange={e => setPauseConfig(c => ({ ...c, adjoint_pause_totale: parseFloat(e.target.value) || 0 }))} className="w-24" />
                    <Badge variant="outline" className="text-xs">{(pauseConfig.adjoint_pause_totale * 60).toFixed(0)} min</Badge>
                  </div>
                  <p className="text-xs text-slate-500 mt-0.5">Durée "normale" de pause (utilisée si travail &gt; 6h)</p>
                </div>
                <div>
                  <Label className="text-xs">Pause payée (maximum)</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <Input type="number" step="0.25" min="0" max="2" value={pauseConfig.adjoint_pause_payee}
                      onChange={e => setPauseConfig(c => ({ ...c, adjoint_pause_payee: parseFloat(e.target.value) || 0 }))} className="w-24" />
                    <Badge variant="outline" className="text-xs">{(pauseConfig.adjoint_pause_payee * 60).toFixed(0)} min payée</Badge>
                  </div>
                </div>
                <div className="bg-blue-50 border border-blue-200 rounded p-2 text-xs">
                  <p className="font-semibold text-blue-700 mb-1">📌 Règle de déduction :</p>
                  <p className="text-blue-600">• Pause payée : <strong>maximum {(pauseConfig.adjoint_pause_payee * 60).toFixed(0)} min</strong></p>
                  <p className="text-blue-600">• Pause non payée : <strong>pause totale - {(pauseConfig.adjoint_pause_payee * 60).toFixed(0)} min</strong></p>
                  <p className="text-blue-700 mt-1"><strong>Exemples :</strong></p>
                  <p className="text-blue-600">  - Prend 50 min → 30 min payée, enlève 20 min</p>
                  <p className="text-blue-600">  - Prend 75 min → 30 min payée, enlève 45 min</p>
                  <p className="text-blue-600">  - Prend 2h → 30 min payée, enlève 1h30</p>
                </div>
              </CardContent>
            </Card>

            {/* Propriétaire */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <User className="w-4 h-4 text-emerald-600" /> Propriétaire
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <Label className="text-xs">Pause totale (heures)</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <Input type="number" step="0.25" min="0" max="2" value={pauseConfig.proprio_pause_totale}
                      onChange={e => setPauseConfig(c => ({ ...c, proprio_pause_totale: parseFloat(e.target.value) || 0 }))} className="w-24" />
                    <Badge variant="outline" className="text-xs">{(pauseConfig.proprio_pause_totale * 60).toFixed(0)} min</Badge>
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Dont payée (heures)</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <Input type="number" step="0.25" min="0" max="2" value={pauseConfig.proprio_pause_payee}
                      onChange={e => setPauseConfig(c => ({ ...c, proprio_pause_payee: parseFloat(e.target.value) || 0 }))} className="w-24" />
                    <Badge variant="outline" className="text-xs">{(pauseConfig.proprio_pause_payee * 60).toFixed(0)} min payée</Badge>
                  </div>
                </div>
                <p className="text-xs text-emerald-600">Déduction: {((pauseConfig.proprio_pause_totale - pauseConfig.proprio_pause_payee) * 60).toFixed(0)} min déduite du temps travaillé</p>
              </CardContent>
            </Card>
          </div>

          <Button onClick={savePauseConfig} disabled={savingPause} className="w-full sm:w-auto">
            <Save className="w-4 h-4 mr-2" />
            {savingPause ? 'Sauvegarde...' : 'Sauvegarder les règles de pause'}
          </Button>
        </TabsContent>
      </Tabs>

      {/* 🆕 [Phase v13.60.17 — 2026-08-18] Barre d'action sticky pour la validation batch.
          - Visible seulement si ≥1 punch sélectionné.
          - Sticky bottom avec safe-area-inset-bottom pour ne pas être cachée sur mobile.
          - Boutons : "Valider les N sélectionnés" + "Effacer la sélection". */}
      {selectedPunchIds.size > 0 && (
        <div
          className="fixed left-0 right-0 z-40 bg-green-600 text-white shadow-2xl border-t-2 border-green-800"
          style={{ bottom: 'env(safe-area-inset-bottom, 0)' }}
          data-testid="barre-selection-batch"
        >
          <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-2 min-w-0">
              <CheckCircle className="w-5 h-5 shrink-0" />
              <span className="font-semibold text-sm sm:text-base" data-testid="barre-selection-compteur">
                {selectedPunchIds.size} pointage{selectedPunchIds.size > 1 ? 's' : ''} sélectionné{selectedPunchIds.size > 1 ? 's' : ''}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant="outline"
                className="bg-white text-green-700 hover:bg-green-50 border-white"
                onClick={clearSelection}
                disabled={batchValidating}
                data-testid="btn-clear-selection"
              >
                Effacer
              </Button>
              <Button
                size="sm"
                className="bg-white text-green-700 hover:bg-green-50 font-semibold"
                onClick={() => setShowConfirmBatchValidate(true)}
                disabled={batchValidating}
                data-testid="btn-valider-batch-selection"
              >
                <CheckCircle className="w-4 h-4 mr-1" />
                Valider les {selectedPunchIds.size} punch{selectedPunchIds.size > 1 ? 's' : ''} sélectionné{selectedPunchIds.size > 1 ? 's' : ''}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* 🆕 [Phase v13.60.17] AlertDialog de confirmation pour la validation batch */}
      <AlertDialog open={showConfirmBatchValidate} onOpenChange={setShowConfirmBatchValidate}>
        <AlertDialogContent data-testid="dialog-confirm-batch-validate">
          <AlertDialogHeader>
            <AlertDialogTitle>Valider les pointages sélectionnés ?</AlertDialogTitle>
            <AlertDialogDescription>
              Vous êtes sur le point de valider <strong>{selectedPunchIds.size}</strong> pointage{selectedPunchIds.size > 1 ? 's' : ''}.
              Cette action va intégrer les heures dans les bons de travail correspondants.
              Vous pourrez toujours ajuster individuellement un pointage validé si nécessaire.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={batchValidating} data-testid="btn-cancel-batch-validate">
              Annuler
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleValidateSelection}
              disabled={batchValidating}
              className="bg-green-600 hover:bg-green-700"
              data-testid="btn-confirm-batch-validate"
            >
              {batchValidating ? 'Validation…' : `Valider les ${selectedPunchIds.size}`}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* 🆕 [Phase 80.1+80.2 — 2026-06-22] Dialog "Test pause PM" (Owner-only)
          Création de punchs fictifs marqués `is_test_punch=true`. Phase 80.2 :
          punch PERSISTANT pour audit dans Rapport d'heures (suppression manuelle). */}
      {isOwner && showTestPausePmDialog && (
        <div
          className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          onClick={() => !creatingTestPunch && setShowTestPausePmDialog(false)}
          data-testid="dialog-test-pause-pm-backdrop"
        >
          <div
            className="bg-white rounded-lg p-6 max-w-md w-full shadow-xl"
            onClick={(e) => e.stopPropagation()}
            data-testid="dialog-test-pause-pm"
          >
            <h3 className="text-lg font-semibold mb-2">🧪 Test pause PM</h3>
            <p className="text-sm text-slate-600 mb-4">
              Crée un punch fictif pour ton compte avec le scénario choisi.
              Le punch apparaît dans la liste avec un badge orange{' '}
              <span className="text-orange-700 bg-orange-100 border border-orange-300 px-1 rounded text-xs font-bold">🧪 TEST</span>,
              et reste visible dans Rapport d'heures (audit) jusqu'à suppression manuelle.
              Aucun impact sur paie / bonus / stats.
            </p>
            <div className="space-y-2">
              <Button
                variant="outline"
                className="w-full justify-start"
                onClick={() => handleCreateTestPunch('7h', null)}
                disabled={creatingTestPunch}
                data-testid="btn-scenario-7h"
              >
                <span className="text-slate-700">Simuler 7h00 brut</span>
                <span className="text-xs text-slate-500 ml-auto">→ Sous-seuil (gris)</span>
              </Button>
              <Button
                variant="outline"
                className="w-full justify-start"
                onClick={() => handleCreateTestPunch('7h45', 'oui')}
                disabled={creatingTestPunch}
                data-testid="btn-scenario-7h45-oui"
              >
                <span className="text-slate-700">7h45 brut + Oui</span>
                <span className="text-xs text-emerald-600 ml-auto">→ Vert (pause prise)</span>
              </Button>
              <Button
                variant="outline"
                className="w-full justify-start"
                onClick={() => handleCreateTestPunch('7h45', 'non')}
                disabled={creatingTestPunch}
                data-testid="btn-scenario-7h45-non"
              >
                <span className="text-slate-700">7h45 brut + Non</span>
                <span className="text-xs text-amber-600 ml-auto">→ Orange (+15 min)</span>
              </Button>
              <Button
                variant="outline"
                className="w-full justify-start"
                onClick={() => handleCreateTestPunch('8h', 'oui')}
                disabled={creatingTestPunch}
                data-testid="btn-scenario-8h-oui"
              >
                <span className="text-slate-700">8h00 brut + Oui</span>
                <span className="text-xs text-emerald-600 ml-auto">→ Vert (pause prise)</span>
              </Button>
              <Button
                variant="outline"
                className="w-full justify-start"
                onClick={() => handleCreateTestPunch('8h', 'non')}
                disabled={creatingTestPunch}
                data-testid="btn-scenario-8h-non"
              >
                <span className="text-slate-700">8h00 brut + Non</span>
                <span className="text-xs text-amber-600 ml-auto">→ Orange (+15 min)</span>
              </Button>
              <Button
                variant="outline"
                className="w-full justify-start"
                onClick={() => handleCreateTestPunch('8h', null)}
                disabled={creatingTestPunch}
                data-testid="btn-scenario-8h-zombie"
              >
                <span className="text-slate-700">8h00 brut sans réponse</span>
                <span className="text-xs text-red-600 ml-auto">→ Rouge (zombie)</span>
              </Button>
            </div>
            <div className="flex justify-end gap-2 mt-4 pt-3 border-t">
              <Button
                variant="ghost"
                onClick={() => setShowTestPausePmDialog(false)}
                disabled={creatingTestPunch}
                data-testid="btn-test-pause-pm-cancel"
              >
                Annuler
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ValidationHeures;
